import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api';
import { API_BASE } from './apiClient';
import type {
  ArchiveEvent,
  AssetCondition,
  Category,
  ChannelResult,
  CollectionConditionDetail,
  FormType,
  GuidelineFolder,
  GuidelineStep,
  GuidelineTool,
  Me,
  NavigationTab,
  Permission,
  RankingResponse,
  RecommendationRun,
  Role,
  UserRecord,
  VideoResult,
} from './types';

type LoadState<T> =
  | { status: 'idle' | 'loading'; data: T | null; error: '' }
  | { status: 'ready'; data: T; error: '' }
  | { status: 'error'; data: T | null; error: string };

type ExploreSubtab = 'channel' | 'video' | 'keyword' | 'topic' | 'category';
type AssetTarget = 'recommendation' | 'example';
type Notice = { tone: 'ok' | 'error'; text: string } | null;
type SimilarState =
  | {
      kind: 'channel';
      source: ChannelResult | VideoResult;
      status: 'loading' | 'ready' | 'error';
      result: (RankingResponse<ChannelResult> & { sourceChannel?: ChannelResult | null }) | null;
      error: string;
    }
  | {
      kind: 'video';
      source: VideoResult;
      status: 'loading' | 'ready' | 'error';
      result: (RankingResponse<VideoResult> & { sourceVideo?: VideoResult | null }) | null;
      error: string;
    };
type ChannelRankingPin = {
  source: string;
  country: string;
  categoryKey: string;
  period: string;
  date?: string;
  limit: number;
  formType: FormType;
  label: string;
  savedAt: number;
};

const defaultTabs: NavigationTab[] = [
  { key: 'recommendation', label: '추천' },
  { key: 'example', label: '예시' },
  { key: 'archive', label: '확정' },
  { key: 'guideline', label: '지침' },
];

const adminRoutes: Record<string, string> = {
  explore: '/admin/explore',
  asset: '/admin/assets',
  collect: '/admin/collect',
  recommendation: '/admin/recommendations',
  example: '/admin/examples',
  archive: '/admin/archive',
  guideline: '/admin/guidelines',
};

const editorRoutes: Record<string, string> = {
  recommendation: '/editor/recommendations',
  example: '/editor/examples',
  archive: '/editor/archive',
  guideline: '/editor/guidelines',
};

const countries = [
  ['US', '미국'],
  ['KR', '한국'],
  ['JP', '일본'],
  ['TW', '타이완'],
  ['WORLD', '전세계'],
];

const rankingPeriods = [
  ['daily', '일간'],
  ['weekly', '주간'],
  ['monthly', '월간'],
  ['yearend', '연말'],
  ['yearly', '연간'],
  ['total', '전체'],
];

const youtubePeriods = [
  ['1d', '1일'],
  ['2d', '2일'],
  ['3d', '3일'],
  ['7d', '7일'],
  ['14d', '14일'],
  ['28d', '28일'],
  ['after28d', '28일 이후'],
  ['3m', '3개월'],
  ['6m', '6개월'],
  ['1y', '1년'],
  ['3y', '3년'],
  ['all', '전체'],
];

const videoSorts = [
  ['viewCount', '조회수순'],
  ['relevance', '관련성순'],
  ['date', '최신 업로드순'],
  ['rating', '평점순'],
  ['title', '제목순'],
];

const videoRankingSources = [
  ['pick7_video_ranking', 'PICK7 영상랭킹'],
  ['playboard_shorts', 'Playboard Shorts'],
  ['viewstats_top_videos', 'ViewStats Top Videos'],
  ['vling_algorithm_videos', 'Vling Algorithm Videos'],
];

const channelRankingSources = [
  ['pick7_channel_ranking', 'PICK7 채널랭킹'],
  ['playboard_channels', 'Playboard Channels'],
  ['vling_shorts_channels', 'Vling Shorts Channels'],
];

function App() {
  const formType: FormType = 'shorts';
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [devEmail, setDevEmail] = useState(() => localStorage.getItem('pick7.devEmail') || '');
  const [meState, setMeState] = useState<LoadState<Me>>({ status: 'loading', data: null, error: '' });
  const [tabsState, setTabsState] = useState<LoadState<NavigationTab[]>>({ status: 'loading', data: null, error: '' });
  const [activeTab, setActiveTab] = useState('recommendation');
  const [notice, setNotice] = useState<Notice>(null);
  const [editorScope, setEditorScope] = useState('');
  const [editors, setEditors] = useState<UserRecord[]>([]);
  const [ytKeyCount, setYtKeyCount] = useState<number | null>(null);
  const tabs = tabsState.data || defaultTabs;
  const me = meState.data;
  const selectedEditor = editors.find((editor) => editor.email === editorScope) || null;

  useEffect(() => {
    document.body.dataset.fmt = 'short';
  }, []);

  useEffect(() => {
    if (me?.role !== 'admin') {
      setEditors([]);
      setEditorScope('');
      return undefined;
    }
    let active = true;
    api.users()
      .then((data) => {
        if (!active) return;
        const activeEditors = (data.users || []).filter((user) => user.role === 'editor' && Number(user.is_active) === 1);
        setEditors(activeEditors);
        setEditorScope((current) => activeEditors.some((editor) => editor.email === current)
          ? current
          : (activeEditors[0]?.email || ''));
      })
      .catch(() => {
        if (active) setEditors([]);
      });
    api.keys()
      .then((keys) => { if (active) setYtKeyCount((keys || []).length); })
      .catch(() => { if (active) setYtKeyCount(null); });
    return () => { active = false; };
  }, [me?.role, devEmail, settingsOpen]);

  useEffect(() => {
    let active = true;
    async function loadShell() {
      setMeState({ status: 'loading', data: null, error: '' });
      setTabsState({ status: 'loading', data: null, error: '' });
      try {
        const [me, navigation] = await Promise.all([api.me(), api.navigation()]);
        if (!active) return;
        setMeState({ status: 'ready', data: me, error: '' });
        setTabsState({ status: 'ready', data: navigation.tabs, error: '' });
        const nextTab = resolveTabForPath(window.location.pathname, me.role, navigation.tabs);
        setActiveTab(nextTab);
        replaceAppPath(pathForTab(me.role, nextTab));
      } catch (error) {
        if (!active) return;
        const message = error instanceof Error ? error.message : '로그인이 필요합니다';
        setMeState({ status: 'error', data: null, error: message });
        setTabsState({ status: 'error', data: defaultTabs, error: message });
      }
    }
    loadShell();
    return () => { active = false; };
  }, [devEmail]);

  useEffect(() => {
    if (!notice) return;
    const id = window.setTimeout(() => setNotice(null), 2400);
    return () => window.clearTimeout(id);
  }, [notice]);

  useEffect(() => {
    if (!me || !tabs.length) return undefined;
    const handlePopState = () => {
      setActiveTab(resolveTabForPath(window.location.pathname, me.role, tabs));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [me, tabs]);

  return (
    <>
      <header className="hdr">
        <div className="logo" aria-label="Pick7">
          <em>🎬</em>
          <b>Pick7</b>
        </div>
        <div className="fmt-toggle" role="group" aria-label="폼타입">
          <button className="fmt-btn active" disabled>숏폼</button>
        </div>
        <div className="hdr-spacer" />
        <div className="hdr-right">
          <div className="key-dots" title="API 상태">
            <span className={`kdot ${meState.status === 'ready' ? 'on' : ''}`} />
            <span className={`kdot ${(ytKeyCount || 0) > 0 ? 'on' : ''}`} />
            <span className={`kdot ${meState.status === 'error' ? 'err' : ''}`} />
          </div>
          <span className="key-txt">{me?.role === 'admin' && ytKeyCount != null ? `YT·${ytKeyCount}` : (me?.email || 'not signed')}</span>
          {me?.role === 'admin' && (
            <EditorDropdown
              editors={editors}
              editorScope={editorScope}
              onSelect={setEditorScope}
              onOpenSettings={() => setSettingsOpen(true)}
            />
          )}
          {me?.role === 'admin' && editorScope && (
            <span className="workspace-indicator">
              현재 작업공간: {selectedEditor?.display_name || editorScope} ({editorScope})
            </span>
          )}
          {me?.role === 'admin' && <span className="admin-badge">관리자</span>}
          <button className={`cfg-btn ${settingsOpen ? 'open' : ''}`} title="설정" onClick={() => setSettingsOpen((value) => !value)}>⚙</button>
        </div>
      </header>

      <nav className="tab-bar">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={`hdr-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.key);
              window.history.pushState(null, '', pathForTab(me?.role || 'editor', tab.key));
            }}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {settingsOpen && (
        <SettingsPanel
          devEmail={devEmail}
          setDevEmail={setDevEmail}
          role={me?.role || 'editor'}
          notify={setNotice}
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {notice && <div className={`toast ${notice.tone}`}>{notice.text}</div>}

      <main className="app-main">
        {meState.status === 'error' ? (
          <AuthPanel error={meState.error} devEmail={devEmail} setDevEmail={setDevEmail} />
        ) : (
          <DashboardPanel
            key={`${me?.role || 'editor'}:${editorScope || me?.email || ''}`}
            activeTab={activeTab}
            role={me?.role || 'editor'}
            userEmail={me?.email || devEmail || 'anonymous'}
            formType={formType}
            editorScope={editorScope}
            editorPermissions={selectedEditor?.permissions || []}
            notify={setNotice}
          />
        )}
      </main>
    </>
  );
}

// admin-demo v2 헤더의 편집자 접근 관리 드롭다운.
// 단순 미리보기가 아니라 현재 관리할 editor_email scope 선택기다.
const EDITOR_AVATAR_GRADIENTS = [
  'linear-gradient(135deg,#4c8dff,#2dd4a8)',
  'linear-gradient(135deg,#a78bfa,#4c8dff)',
  'linear-gradient(135deg,#f0b429,#ff4466)',
];

function editorGradient(index: number) {
  return EDITOR_AVATAR_GRADIENTS[((index % EDITOR_AVATAR_GRADIENTS.length) + EDITOR_AVATAR_GRADIENTS.length) % EDITOR_AVATAR_GRADIENTS.length];
}

function EditorDropdown({
  editors,
  editorScope,
  onSelect,
  onOpenSettings,
}: {
  editors: UserRecord[];
  editorScope: string;
  onSelect: (email: string) => void;
  onOpenSettings: () => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedIndex = editors.findIndex((editor) => editor.email === editorScope);
  const selected = selectedIndex >= 0 ? editors[selectedIndex] : null;

  useEffect(() => {
    if (!open) return undefined;
    const close = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('.editor-dd-wrap') || target.closest('.editor-panel')) return;
      setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);

  return (
    <div className="editor-dd-wrap">
      <button className={`editor-dd-btn ${open ? 'open' : ''}`} onClick={() => setOpen((value) => !value)}>
        <span
          className="ed-avatar"
          style={{ background: selected ? editorGradient(selectedIndex) : 'var(--bg4)' }}
        >
          {selected ? (selected.display_name || selected.email)[0] : '?'}
        </span>
        <span>{selected ? (selected.display_name || selected.email) : '편집자 선택'}</span>
        <span className="ed-chevron">▼</span>
      </button>
      <div className={`editor-panel ${open ? 'on' : ''}`}>
        <div className="ep-hdr">
          <div className="ep-title">편집자 접근 관리</div>
        </div>
        <div className="ep-list">
          {editors.map((editor, index) => (
            <div
              className={`ep-item ${editor.email === editorScope ? 'selected' : ''}`}
              key={editor.id}
              onClick={() => {
                onSelect(editor.email);
                window.setTimeout(() => setOpen(false), 180);
              }}
            >
              <div className="ep-av" style={{ background: editorGradient(index) }}>
                {(editor.display_name || editor.email)[0]}
              </div>
              <div className="ep-info">
                <div className="ep-name">{editor.display_name || editor.email}</div>
                <div className="ep-email">{editor.email}</div>
              </div>
              <div className="ep-status">
                <span className={`ep-dot ${Number(editor.is_active) === 1 ? 'active' : 'pending'}`} title={Number(editor.is_active) === 1 ? '앱에 등록됨 · Cloudflare Access 허용 정책은 별도 확인' : '앱에서 비활성화됨'} />
                {editor.email === editorScope && <span className="ep-check">✓</span>}
              </div>
            </div>
          ))}
          {!editors.length && <div className="empty-meta" style={{ padding: '10px' }}>활성 편집자가 없습니다</div>}
        </div>
        <div className="ep-foot">
          <button className="ep-add-btn" onClick={() => { setOpen(false); onOpenSettings(); }}>+ 편집자 추가</button>
          <button className="ep-manage-btn" onClick={() => { setOpen(false); onOpenSettings(); }}>관리</button>
        </div>
      </div>
    </div>
  );
}

function DashboardPanel({
  activeTab,
  role,
  userEmail,
  formType,
  editorScope,
  editorPermissions,
  notify,
}: {
  activeTab: string;
  role: Role;
  userEmail: string;
  formType: FormType;
  editorScope: string;
  editorPermissions: Permission[];
  notify: (notice: Notice) => void;
}) {
  if (role === 'admin' && !editorScope) {
    return <EmptyPanel title="편집자를 선택하면 작업공간이 열립니다" />;
  }
  if (activeTab === 'explore') return <ExploreView userEmail={userEmail} formType={formType} editorScope={editorScope} editorPermissions={editorPermissions} notify={notify} />;
  if (activeTab === 'asset') return <AssetsView editorScope={editorScope} notify={notify} />;
  if (activeTab === 'collect') return <CollectionView formType={formType} editorScope={editorScope} editorPermissions={editorPermissions} notify={notify} />;
  if (activeTab === 'recommendation') return <RecommendationsView role={role} formType={formType} editorScope={editorScope} notify={notify} />;
  if (activeTab === 'example') return <ExamplesView formType={formType} editorScope={editorScope} notify={notify} />;
  if (activeTab === 'archive') return <ArchiveView formType={formType} editorScope={editorScope} notify={notify} />;
  if (activeTab === 'guideline') return <GuidelinesView role={role} editorScope={editorScope} notify={notify} />;
  return <EmptyPanel title="준비 중" />;
}

function ExploreView({ userEmail, formType, editorScope, editorPermissions, notify }: { userEmail: string; formType: FormType; editorScope: string; editorPermissions: Permission[]; notify: (notice: Notice) => void }) {
  const categories = useApiData('categories', api.categories);
  const [subtab, setSubtab] = useState<ExploreSubtab>('channel');
  const [query, setQuery] = useState('');
  const [country, setCountry] = useState('US');
  const [categoryKey, setCategoryKey] = useState('all');
  const [period, setPeriod] = useState('daily');
  const [rankingDate, setRankingDate] = useState('');
  const [youtubePeriod, setYoutubePeriod] = useState('7d');
  const [sort, setSort] = useState('viewCount');
  const [channelSource, setChannelSource] = useState('pick7_channel_ranking');
  const [videoSource, setVideoSource] = useState('pick7_video_ranking');
  const [limit, setLimit] = useState(10);
  const [view, setView] = useState<'card' | 'table'>('table');
  const [keywordResultKind, setKeywordResultKind] = useState<'video' | 'channel'>('video');
  const [result, setResult] = useState<RankingResponse<VideoResult | ChannelResult> | null>(null);
  const [loading, setLoading] = useState(false);
  const [similar, setSimilar] = useState<SimilarState | null>(null);
  const [pinnedChannelRanking, setPinnedChannelRanking] = useState<ChannelRankingPin | null>(null);
  const allowedCategories = filterCategoriesForPermissions(categories.data?.categories || [], editorPermissions, formType);

  const selectedCategory = allowedCategories.find((category) => category.category_key === categoryKey);
  const source = subtab === 'channel' ? channelSource : videoSource;
  const isChannelRankingMode = subtab === 'channel' && !query.trim();
  const isRankingCondition = isChannelRankingMode || subtab === 'video';
  const conditionPeriod = isRankingCondition ? period : youtubePeriod;
  const conditionDate = isRankingCondition ? rankingDate : '';
  const conditionSort = isChannelRankingMode ? 'dailyViews' : subtab === 'video' ? 'views' : sort;
  const conditionSource = isRankingCondition ? source : subtab === 'channel' ? 'youtube_channel_search' : 'youtube_search';
  const resultItems = result?.items || [];
  const conditionLabel = makeConditionLabel({
    subtab,
    query,
    country,
    categoryLabel: selectedCategory?.label || '전체',
    period: conditionPeriod,
    date: conditionDate,
    sort: conditionSort,
    limit,
  });
  const channelRankingPin: ChannelRankingPin = {
    source,
    country,
    categoryKey,
    period,
    date: rankingDate,
    limit,
    formType,
    label: conditionLabel,
    savedAt: Date.now(),
  };
  const isChannelRankingResult = isChannelRankingMode && result?.itemType === 'channel';
  const isCurrentChannelRankingPinned = Boolean(isChannelRankingResult && pinnedChannelRanking && sameChannelRankingPin(pinnedChannelRanking, channelRankingPin));

  useEffect(() => {
    let active = true;
    api.settings()
      .then(({ settings }) => {
        if (!active) return;
        const saved = parseChannelRankingPin(settings[channelRankingPinKey(userEmail, formType)], formType);
        setPinnedChannelRanking(saved);
        if (!saved) return;
        setSubtab('channel');
        setQuery('');
        setChannelSource(saved.source || 'pick7_channel_ranking');
        setCountry(saved.country || 'US');
        setCategoryKey(saved.categoryKey || 'all');
        setPeriod(saved.period || 'daily');
        setRankingDate(saved.date || '');
        setLimit(saved.limit || 10);
        setResult(null);
      })
      .catch(() => { if (active) setPinnedChannelRanking(null); });
    return () => { active = false; };
  }, [userEmail, formType]);

  useEffect(() => {
    if (!allowedCategories.length) return;
    if (!allowedCategories.some((category) => category.category_key === categoryKey)) {
      setCategoryKey(allowedCategories[0].category_key);
      setResult(null);
    }
  }, [categoryKey, allowedCategories]);

  async function runExplore() {
    setLoading(true);
    try {
      let next: RankingResponse<VideoResult | ChannelResult>;
      if (subtab === 'channel' && query.trim()) {
        next = await api.searchChannels({ q: query.trim(), country, limit });
      } else if (subtab === 'keyword') {
        if (!query.trim()) throw new Error('검색할 키워드를 입력해주세요');
        next = await api.searchVideos({
          q: query.trim(),
          country,
          videoCategoryId: selectedCategory?.youtube_id || '',
          order: sort,
          period: youtubePeriod,
          limit,
        });
        setKeywordResultKind('video');
      } else if (subtab === 'topic' || subtab === 'category') {
        const term = subtab === 'topic' ? (query.trim() || selectedCategory?.label || '엔터테인먼트') : (selectedCategory?.label || query.trim());
        next = await api.searchVideos({
          q: term,
          country,
          videoCategoryId: selectedCategory?.youtube_id || '',
          order: sort,
          period: youtubePeriod,
          limit,
        });
      } else {
        next = await api.rankings({
          itemType: subtab === 'channel' ? 'channel' : 'video',
          source,
          country,
          category: categoryKey,
          period,
          date: rankingDate,
          sort: subtab === 'channel' ? 'dailyViews' : 'views',
          videoType: formType === 'shorts' ? 'shorts' : 'normal',
        });
      }
      setResult(next);
      notify({ tone: 'ok', text: `${next.count || next.items.length}개 결과를 불러왔습니다` });
    } catch (error) {
      setResult(null);
      notify({ tone: 'error', text: error instanceof Error ? error.message : '탐색 실패' });
    } finally {
      setLoading(false);
    }
  }

  async function registerCurrentCondition(target: AssetTarget) {
    if (!editorScope) {
      notify({ tone: 'error', text: '편집자를 먼저 선택해주세요' });
      return;
    }
    try {
      const conditionType = subtab === 'video' ? 'ranking' : subtab;
      await api.createAsset({
        target,
        editorEmail: editorScope,
        conditionType,
        label: conditionLabel,
        source: conditionSource,
        provider: isRankingCondition ? 'ranking' : 'youtube',
        country,
        categoryKey,
        formType,
        sortKey: conditionSort,
        limitCount: limit,
        payload: {
          subtab,
          query: query.trim(),
          keyword: subtab === 'keyword' ? query.trim() : undefined,
          source: conditionSource,
          country,
          category: categoryKey,
          categoryLabel: selectedCategory?.label || '전체',
          videoCategoryId: selectedCategory?.youtube_id || '',
          period: conditionPeriod,
          date: conditionDate,
          sort: conditionSort,
          limit,
          formType,
        },
      });
      notify({ tone: 'ok', text: `${target === 'recommendation' ? '추천' : '예시'} 에셋에 조건을 등록했습니다` });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '에셋 등록 실패' });
    }
  }

  async function registerChannelCondition(target: AssetTarget, channel: ChannelResult) {
    if (!editorScope) {
      notify({ tone: 'error', text: '편집자를 먼저 선택해주세요' });
      return;
    }
    try {
      await api.createAsset({
        target,
        editorEmail: editorScope,
        conditionType: 'channel',
        label: channel.channelTitle || channel.title || channel.channelId,
        normalizedKey: channel.channelId,
        source: 'youtube_channel',
        provider: 'youtube',
        country,
        categoryKey,
        formType,
        sortKey: 'mixed',
        limitCount: 20,
        payload: {
          channelId: channel.channelId,
          channelTitle: channel.channelTitle || channel.title,
          channelUrl: channel.youtubeUrl,
          fetchMode: 'both',
          latestCount: 10,
          popularCount: 10,
          period: youtubePeriod,
          sort: 'mixed',
          formType,
        },
      });
      notify({ tone: 'ok', text: `${target === 'recommendation' ? '추천' : '예시'} 에셋에 채널 조건을 등록했습니다` });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '채널 조건 등록 실패' });
    }
  }

  async function toggleChannelRankingPin() {
    if (!isChannelRankingResult) return;
    try {
      if (isCurrentChannelRankingPinned) {
        await api.updateSetting(channelRankingPinKey(userEmail, formType), null);
        setPinnedChannelRanking(null);
        notify({ tone: 'ok', text: '채널 랭킹 조건 고정을 해제했습니다' });
        return;
      }
      const next = { ...channelRankingPin, savedAt: Date.now() };
      await api.updateSetting(channelRankingPinKey(userEmail, formType), next);
      setPinnedChannelRanking(next);
      notify({ tone: 'ok', text: '채널 랭킹 조건을 고정했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '고정 저장 실패' });
    }
  }

  async function archiveVideo(video: VideoResult) {
    if (!editorScope) {
      notify({ tone: 'error', text: '편집자를 먼저 선택해주세요' });
      return;
    }
    try {
      await api.addArchive({
        ...videoArchivePayload(video),
        archived_for_email: editorScope,
        form_type: formType,
        category_key: categoryKey,
      });
      notify({ tone: 'ok', text: '확정에 등록했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '확정 등록 실패' });
    }
  }

  async function openSimilarChannel(item: ChannelResult | VideoResult) {
    const channelId = resultChannelId(item);
    const title = resultChannelTitle(item);
    if (!channelId && !title) {
      notify({ tone: 'error', text: '유사 채널 기준이 없습니다' });
      return;
    }
    setSimilar({ kind: 'channel', source: item, status: 'loading', result: null, error: '' });
    try {
      const next = await api.similarChannels({ channelId, q: title, country, limit: 10 });
      setSimilar({ kind: 'channel', source: item, status: 'ready', result: next, error: '' });
    } catch (error) {
      setSimilar({ kind: 'channel', source: item, status: 'error', result: null, error: error instanceof Error ? error.message : '유사 채널 찾기 실패' });
    }
  }

  async function openSimilarVideo(item: VideoResult) {
    const id = videoId(item);
    if (!id) {
      notify({ tone: 'error', text: '유사 영상 기준 ID가 없습니다' });
      return;
    }
    setSimilar({ kind: 'video', source: item, status: 'loading', result: null, error: '' });
    try {
      const next = await api.similarVideos({ videoId: id, q: item.title || '', country, limit: 5 });
      setSimilar({ kind: 'video', source: item, status: 'ready', result: next, error: '' });
    } catch (error) {
      setSimilar({ kind: 'video', source: item, status: 'error', result: null, error: error instanceof Error ? error.message : '유사 영상 찾기 실패' });
    }
  }

  if (similar) {
    return (
      <SimilarityPanel
        state={similar}
        onBack={() => setSimilar(null)}
        onRegisterChannel={registerChannelCondition}
        onArchiveVideo={archiveVideo}
        onSimilarChannel={openSimilarChannel}
        onSimilarVideo={openSimilarVideo}
      />
    );
  }

  const subtabMeta: Record<ExploreSubtab, { icon: string; label: string; kind: string }> = {
    channel: { icon: '📡', label: '채널 탐색', kind: '📡 채널 랭킹' },
    video: { icon: '🎞️', label: '영상 탐색', kind: '🎞️ 영상 랭킹' },
    keyword: { icon: '🔎', label: '키워드 탐색', kind: '🔎 키워드 탐색' },
    topic: { icon: '📚', label: '토픽 탐색', kind: '📚 토픽 탐색' },
    category: { icon: '🗂️', label: '카테고리 탐색', kind: '🗂️ 카테고리 탐색' },
  };

  return (
    <section className="panel active">
      <div className="sub-tabs">
        {(Object.keys(subtabMeta) as ExploreSubtab[]).map((key) => (
          <button
            key={key}
            className={`sub-tab ${subtab === key ? 'active' : ''}`}
            onClick={() => {
              setSubtab(key);
              setKeywordResultKind('video');
              setResult(null);
            }}
          >
            {subtabMeta[key].icon} {subtabMeta[key].label}
          </button>
        ))}
        <div className="sub-r">
          <span className="sec-meta">{result ? `${resultItems.length}개 결과` : '탐색 전'}</span>
          <button className="btn-secondary btn-sm" onClick={() => void runExplore()} disabled={loading || !result}>↻ 갱신</button>
        </div>
      </div>
      <div className="content">
        <div className="rank-search-bar">
          <span className="rank-filter-lbl">🔎 검색</span>
          <input
            className="rank-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') void runExplore(); }}
            placeholder={subtab === 'channel' ? '채널명/핸들 검색' : subtab === 'keyword' ? '키워드 검색' : '검색어 선택 사항'}
          />
          <button className="rank-search-btn" type="button" onClick={() => void runExplore()}>검색</button>
        </div>
        <div className="rank-filters">
          <span className="rank-live-kind-label">{subtabMeta[subtab].kind}</span>
          {(subtab === 'channel' || subtab === 'video') && (
            <>
              <span className="rank-filter-lbl">소스</span>
              <Select
                value={subtab === 'channel' ? channelSource : videoSource}
                onChange={subtab === 'channel' ? setChannelSource : setVideoSource}
                options={subtab === 'channel' ? channelRankingSources : videoRankingSources}
                wide
              />
            </>
          )}
          <span className="rank-filter-lbl">국가</span>
          <Select value={country} onChange={setCountry} options={countries} />
          <span className="rank-filter-lbl">카테고리</span>
          <Select
            value={categoryKey}
            onChange={setCategoryKey}
            options={allowedCategories.map((category) => [category.category_key, category.label])}
            wide
          />
          <span className="rank-filter-lbl">정렬</span>
          <Select
            value={sort}
            onChange={setSort}
            options={videoSorts}
            wide
            disabled={subtab === 'channel' || subtab === 'video'}
          />
          <span className="rank-filter-lbl">결과</span>
          <NumberInput value={limit} onChange={setLimit} min={1} max={20} />
          <span className="rank-filter-lbl">기간</span>
          <Select
            value={subtab === 'channel' || subtab === 'video' ? period : youtubePeriod}
            onChange={(value) => (subtab === 'channel' || subtab === 'video' ? setPeriod(value) : setYoutubePeriod(value))}
            options={subtab === 'channel' || subtab === 'video' ? rankingPeriods : youtubePeriods}
          />
          {isRankingCondition && (
            <input
              className="rank-input date"
              type="date"
              value={rankingDate}
              onChange={(event) => setRankingDate(event.target.value)}
              title="날짜"
            />
          )}
          <button
            type="button"
            data-explore-run
            className="expl-collect-btn"
            onClick={() => void runExplore()}
            disabled={loading}
          >
            {loading ? '불러오는 중' : '탐색'}
          </button>
          {result?.sourceUrl && <a className="rank-live-url" href={result.sourceUrl} target="_blank" rel="noreferrer">원본 URL 열기</a>}
          {isChannelRankingResult && (
            <button
              className={`rank-live-url rank-live-pin ${isCurrentChannelRankingPinned ? 'pinned' : ''}`}
                onClick={() => void toggleChannelRankingPin()}
            >
              {isCurrentChannelRankingPinned ? '고정됨' : '이 조건을 고정'}
            </button>
          )}
          <div className="toolbar-right">
            <ViewToggle value={view} onChange={setView} />
          </div>
        </div>

        {result && (
          <div className="condition-register-row">
            <span className="condition-help">{conditionLabel} · 이 조건을</span>
            <button className="btn-secondary btn-sm green" onClick={() => registerCurrentCondition('recommendation')}>추천 에셋 등록</button>
            <button className="btn-secondary btn-sm purple" onClick={() => registerCurrentCondition('example')}>예시 에셋 등록</button>
          </div>
        )}

        {!result && !loading && <EmptyPanel title="탐색 전입니다" />}
        {loading && <EmptyPanel title="불러오는 중" />}
        {result && result.itemType === 'channel' && (
          <ChannelResults
            items={resultItems as ChannelResult[]}
            view={view}
            onRegister={registerChannelCondition}
            onSimilar={openSimilarChannel}
          />
        )}
        {result && result.itemType === 'video' && subtab === 'keyword' && (
          <>
            <SubTabs tabs={[['video', '영상'], ['channel', '채널']]} active={keywordResultKind} onChange={(key) => setKeywordResultKind(key as 'video' | 'channel')} />
            {keywordResultKind === 'channel' ? (
              <ChannelResults
                items={videosToKeywordChannels(resultItems as VideoResult[])}
                view={view}
                onRegister={registerChannelCondition}
                onSimilar={openSimilarChannel}
              />
            ) : (
              <VideoResults
                items={resultItems as VideoResult[]}
                view={view}
                mode="explore"
                onArchive={archiveVideo}
                onSimilarChannel={openSimilarChannel}
                onSimilarVideo={openSimilarVideo}
              />
            )}
          </>
        )}
        {result && result.itemType === 'video' && subtab !== 'keyword' && (
          <VideoResults
            items={resultItems as VideoResult[]}
            view={view}
            mode="explore"
            onArchive={archiveVideo}
            onSimilarChannel={openSimilarChannel}
            onSimilarVideo={openSimilarVideo}
          />
        )}
      </div>
    </section>
  );
}

function AssetsView({ editorScope, notify }: { editorScope: string; notify: (notice: Notice) => void }) {
  const [target, setTarget] = useState<AssetTarget>('recommendation');
  const [refresh, setRefresh] = useState(0);
  const assets = useApiData(`assets-${target}-${editorScope}-${refresh}`, () => api.assets({ target, editorEmail: editorScope }));
  const conditions = assets.data?.conditions || [];
  const groups = groupConditions(conditions);

  async function togglePinned(condition: AssetCondition) {
    try {
      await api.updateAsset(condition.id, { isPinned: !condition.is_pinned });
      setRefresh((value) => value + 1);
      notify({ tone: 'ok', text: condition.is_pinned ? '고정을 해제했습니다' : '고정했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '고정 변경 실패' });
    }
  }

  async function remove(condition: AssetCondition) {
    try {
      await api.deleteAsset(condition.id);
      setRefresh((value) => value + 1);
      notify({ tone: 'ok', text: '에셋 조건을 비활성화했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '삭제 실패' });
    }
  }

  return (
    <section className="panel active">
      <SubTabs tabs={[['recommendation', '추천 에셋'], ['example', '예시 에셋']]} active={target} onChange={(key) => setTarget(key as AssetTarget)} />
      <div className="asset-wrap">
        {assets.status === 'loading' && <EmptyPanel title="에셋을 불러오는 중" />}
        {assets.status === 'error' && <ErrorBox text={assets.error} />}
        {assets.status === 'ready' && !conditions.length && <EmptyPanel title="등록된 에셋이 없습니다" />}
        {assets.status === 'ready' && conditions.length > 0 && Object.entries(groups).map(([type, rows]) => (
          <div className="asset-sec" key={type}>
            <div className="asset-sec-hdr">
              <div className="asset-sec-title">{assetTypeLabel(type)} 등록됨</div>
              <div className="asset-cnt"><span className="cnt-num">{rows.length}</span>개</div>
            </div>
            <div className="asset-sec-body">
              <div className="condition-list">
                {rows.map((condition) => (
                  <ConditionRow
                    key={condition.id}
                    condition={condition}
                    onPin={() => togglePinned(condition)}
                    onDelete={() => remove(condition)}
                  />
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function CollectionView({ formType, editorScope, editorPermissions, notify }: { formType: FormType; editorScope: string; editorPermissions: Permission[]; notify: (notice: Notice) => void }) {
  const [target, setTarget] = useState<AssetTarget>('recommendation');
  const [country, setCountry] = useState('US');
  const [categoryKey, setCategoryKey] = useState('all');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [refresh, setRefresh] = useState(0);
  const [view, setView] = useState<'card' | 'table'>('table');
  const [similar, setSimilar] = useState<SimilarState | null>(null);
  const collectionRequestKey = useRef('');
  const [collectionState, setCollectionState] = useState<{ status: 'idle' | 'loading' | 'ready' | 'error'; videos: VideoResult[]; error: string; runId: number | null; runStatus: string; details: CollectionConditionDetail[] }>({
    status: 'idle',
    videos: [],
    error: '',
    runId: null,
    runStatus: '',
    details: [],
  });
  const categories = useApiData('collect-categories', api.categories);
  const allowedCategories = filterCategoriesForPermissions(categories.data?.categories || [], editorPermissions, formType);
  const assets = useApiData(`collect-assets-${target}-${editorScope}-${refresh}`, () => api.assets({ target, editorEmail: editorScope }));
  const conditions = assets.data?.conditions || [];

  useEffect(() => {
    setSelected(new Set(conditions.map((condition) => condition.id)));
  }, [conditions]);

  useEffect(() => {
    collectionRequestKey.current = '';
    setCollectionState({ status: 'idle', videos: [], error: '', runId: null, runStatus: '', details: [] });
  }, [target, country, categoryKey, formType, editorScope]);

  useEffect(() => {
    if (!allowedCategories.length) return;
    if (!allowedCategories.some((category) => category.category_key === categoryKey)) {
      setCategoryKey(allowedCategories[0].category_key);
    }
  }, [categoryKey, allowedCategories]);

  async function runCollection() {
    if (!editorScope) {
      notify({ tone: 'error', text: '편집자를 먼저 선택해주세요' });
      return;
    }
    if (collectionState.status === 'loading') return;
    if (!collectionRequestKey.current) collectionRequestKey.current = crypto.randomUUID();
    setCollectionState((current) => ({ ...current, status: 'loading', videos: [], error: '' }));
    try {
      const ids = [...selected];
      const result = await api.createCollectionRun({
        target,
        editorEmail: editorScope,
        formType,
        country,
        categoryKey,
        conditionIds: ids,
        idempotencyKey: collectionRequestKey.current,
      });
      const videos = await api.collectionVideos({ target, editorEmail: editorScope, country, categoryKey, formType });
      setCollectionState({ status: 'ready', videos: videos.videos || [], error: '', runId: result.run.id, runStatus: result.run.status, details: result.details || [] });
      collectionRequestKey.current = '';
      setRefresh((value) => value + 1);
      notify({
        tone: result.run.status === 'failed' ? 'error' : 'ok',
        text: result.run.status === 'partial_failed'
          ? `${result.collected}개 수집 · 일부 조건 실패`
          : result.run.status === 'failed'
            ? '수집 조건이 모두 실패했습니다'
            : `${result.collected}개 영상을 수집했습니다`,
      });
    } catch (error) {
      setCollectionState((current) => ({ ...current, status: 'error', videos: [], error: error instanceof Error ? error.message : '수집 실패' }));
      notify({ tone: 'error', text: error instanceof Error ? error.message : '수집 실패' });
    }
  }

  async function retryCollection() {
    if (!collectionState.runId) return;
    setCollectionState((current) => ({ ...current, status: 'loading', error: '' }));
    try {
      const result = await api.retryCollectionRun(collectionState.runId);
      const videos = await api.collectionVideos({ target, editorEmail: editorScope, country, categoryKey, formType });
      setCollectionState({ status: 'ready', videos: videos.videos || [], error: '', runId: result.run.id, runStatus: result.run.status, details: result.details || [] });
      setRefresh((value) => value + 1);
      notify({ tone: result.run.status === 'failed' ? 'error' : 'ok', text: `재시도 완료 · ${result.collected}개 수집` });
    } catch (error) {
      setCollectionState((current) => ({ ...current, status: 'error', error: error instanceof Error ? error.message : '재시도 실패' }));
      notify({ tone: 'error', text: error instanceof Error ? error.message : '재시도 실패' });
    }
  }

  async function openSimilarChannel(item: ChannelResult | VideoResult) {
    const channelId = resultChannelId(item);
    const title = resultChannelTitle(item);
    if (!channelId && !title) {
      notify({ tone: 'error', text: '유사 채널 기준이 없습니다' });
      return;
    }
    setSimilar({ kind: 'channel', source: item, status: 'loading', result: null, error: '' });
    try {
      const next = await api.similarChannels({ channelId, q: title, country, limit: 10 });
      setSimilar({ kind: 'channel', source: item, status: 'ready', result: next, error: '' });
    } catch (error) {
      setSimilar({ kind: 'channel', source: item, status: 'error', result: null, error: error instanceof Error ? error.message : '유사 채널 찾기 실패' });
    }
  }

  async function openSimilarVideo(item: VideoResult) {
    const id = videoId(item);
    if (!id) {
      notify({ tone: 'error', text: '유사 영상 기준 ID가 없습니다' });
      return;
    }
    setSimilar({ kind: 'video', source: item, status: 'loading', result: null, error: '' });
    try {
      const next = await api.similarVideos({ videoId: id, q: item.title || '', country, limit: 5 });
      setSimilar({ kind: 'video', source: item, status: 'ready', result: next, error: '' });
    } catch (error) {
      setSimilar({ kind: 'video', source: item, status: 'error', result: null, error: error instanceof Error ? error.message : '유사 영상 찾기 실패' });
    }
  }

  async function sendCollectedToRecommendation(item: VideoResult) {
    if (!editorScope) {
      notify({ tone: 'error', text: '편집자를 먼저 선택해주세요' });
      return;
    }
    try {
      const { runs } = await api.recommendationRuns({ formType, editorEmail: editorScope });
      let run = runs.find((candidate) => candidate.run_date === todayKST() && candidate.editor_email === editorScope);
      if (!run) {
        const generated = await api.generateRecommendation({ date: todayKST(), formType, editorEmail: editorScope, categoryKey: 'all' });
        run = generated.run;
      }
      await api.addRecommendationItem(run.id, { videoId: videoId(item) });
      notify({ tone: 'ok', text: '오늘 추천에 등록했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '추천 등록 실패' });
    }
  }

  async function sendCollectedToExample(item: VideoResult) {
    if (!editorScope) {
      notify({ tone: 'error', text: '편집자를 먼저 선택해주세요' });
      return;
    }
    try {
      await api.addExample(videoExamplePayload(item, editorScope, formType, categoryKey));
      notify({ tone: 'ok', text: '예시에 등록했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '예시 등록 실패' });
    }
  }

  async function sendCollectedToArchive(item: VideoResult) {
    if (!editorScope) {
      notify({ tone: 'error', text: '편집자를 먼저 선택해주세요' });
      return;
    }
    try {
      await api.addArchive({
        ...videoArchivePayload(item),
        archived_for_email: editorScope,
        form_type: formType,
        category_key: categoryKey,
      });
      notify({ tone: 'ok', text: '확정에 등록했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '확정 등록 실패' });
    }
  }

  if (similar) {
    return (
      <SimilarityPanel
        state={similar}
        onBack={() => setSimilar(null)}
        onRegisterChannel={async () => undefined}
        onArchiveVideo={sendCollectedToArchive}
        onSimilarChannel={openSimilarChannel}
        onSimilarVideo={openSimilarVideo}
      />
    );
  }

  return (
    <section className="panel active">
      <SubTabs tabs={[['recommendation', '추천 수집'], ['example', '예시 수집']]} active={target} onChange={(key) => setTarget(key as AssetTarget)} />
      <div className="collect-layout">
        <aside className="asset-picker">
          <div className="asset-picker-title">등록된 에셋</div>
          {conditions.map((condition) => (
            <label className="asset-check-row" key={condition.id}>
              <input
                type="checkbox"
                checked={selected.has(condition.id)}
                onChange={(event) => {
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (event.target.checked) next.add(condition.id);
                    else next.delete(condition.id);
                    return next;
                  });
                }}
              />
              <span>{assetTypeIcon(condition.condition_type)} {condition.label}</span>
              <em>{condition.limit_count}</em>
            </label>
          ))}
          {!conditions.length && <div className="empty-meta">등록된 조건이 없습니다</div>}
        </aside>
        <div className="collect-main">
          <div className="filter-band compact">
            <Select value={country} onChange={setCountry} options={countries} />
            <Select
              value={categoryKey}
              onChange={setCategoryKey}
              options={allowedCategories.map((category) => [category.category_key, category.label])}
              wide
            />
            <button className="expl-collect-btn" onClick={runCollection}>수집</button>
            {['failed', 'partial_failed'].includes(collectionState.runStatus) && (
              <button className="btn-secondary btn-sm" onClick={() => void retryCollection()}>실패 조건 재시도</button>
            )}
            <div className="toolbar-right"><ViewToggle value={view} onChange={setView} /></div>
          </div>
          {collectionState.details.length > 0 && (
            <div className="collection-run-summary">
              {collectionState.details.map((detail) => (
                <span className={detail.error ? 'failed' : ''} key={`${collectionState.runId}-${detail.conditionId}`}>
                  {detail.label} · {detail.error
                    ? `실패: ${detail.error}`
                    : `${detail.collected}개${detail.rejectedShorts ? ` · 쇼츠 제외 ${detail.rejectedShorts}` : ''}${detail.uncheckedSkipped ? ` · 판정 보류 ${detail.uncheckedSkipped}` : ''}`}
                </span>
              ))}
            </div>
          )}
          {collectionState.status === 'error' ? (
            <ErrorBox text={collectionState.error} />
          ) : (
            <VideoResults
              items={collectionState.videos}
              view={view}
              mode="collection"
              emptyTitle={collectionState.status === 'loading' ? '수집 중' : collectionState.status === 'idle' ? '수집 전입니다' : '수집된 영상이 없습니다'}
              onRecommendation={sendCollectedToRecommendation}
              onExample={sendCollectedToExample}
              onArchive={sendCollectedToArchive}
              onSimilarChannel={openSimilarChannel}
              onSimilarVideo={openSimilarVideo}
            />
          )}
        </div>
      </div>
    </section>
  );
}

function RecommendationsView({
  role,
  formType,
  editorScope,
  notify,
}: {
  role: Role;
  formType: FormType;
  editorScope: string;
  notify: (notice: Notice) => void;
}) {
  const [sort, setSort] = useState<'latest' | 'popular' | 'pinned'>('latest');
  const [refresh, setRefresh] = useState(0);
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [items, setItems] = useState<VideoResult[]>([]);
  const [noteDrafts, setNoteDrafts] = useState<Record<number, string>>({});
  const [date, setDate] = useState(todayKST());
  const [manualUrl, setManualUrl] = useState('');
  const runs = useApiData(`recs-${formType}-${editorScope}-${refresh}`, () => api.recommendationRuns({ formType, editorEmail: editorScope }));
  // 사이트 전체가 editor_email 스코프: admin은 선택된 편집자의 run만 본다
  const scopedRuns = (runs.data?.runs || []).filter((run) => role !== 'admin' || !editorScope || run.editor_email === editorScope);
  const selectedRun = scopedRuns.find((run) => run.id === selectedRunId) || scopedRuns[0] || null;

  useEffect(() => {
    if (!selectedRun) {
      setItems([]);
      return;
    }
    let active = true;
    api.recommendationRun(selectedRun.id)
      .then((data) => {
        if (!active) return;
        const nextItems = data.items || [];
        setItems(nextItems);
        setNoteDrafts(Object.fromEntries(nextItems.filter((item) => item.id).map((item) => [item.id as number, item.editor_note || ''])));
      })
      .catch((error) => notify({ tone: 'error', text: error instanceof Error ? error.message : '추천 상세 불러오기 실패' }));
    return () => { active = false; };
  }, [selectedRun?.id, notify]);

  async function generate(force = false) {
    if (role === 'admin' && !editorScope) {
      notify({ tone: 'error', text: '편집자를 먼저 선택해주세요' });
      return;
    }
    try {
      const action = force ? api.regenerateRecommendation : api.generateRecommendation;
      const result = await action({ date, formType, categoryKey: 'all', editorEmail: editorScope });
      setSelectedRunId(result.run.id);
      setItems(result.items || []);
      setNoteDrafts(Object.fromEntries((result.items || []).filter((item) => item.id).map((item) => [item.id as number, item.editor_note || ''])));
      setRefresh((value) => value + 1);
      notify({ tone: 'ok', text: force ? '재추천을 생성했습니다' : '추천을 생성했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '추천 생성 실패' });
    }
  }

  async function addManualItem() {
    if (!selectedRun) {
      notify({ tone: 'error', text: '먼저 추천 목록을 생성하거나 선택해주세요' });
      return;
    }
    const url = manualUrl.trim();
    if (!url) {
      notify({ tone: 'error', text: '추가할 YouTube URL을 입력해주세요' });
      return;
    }
    try {
      const result = await api.addRecommendationItem(selectedRun.id, { url });
      setSelectedRunId(result.run.id);
      setItems((prev) => [...prev.filter((item) => item.id !== result.item.id), result.item]
        .sort((a, b) => (a.rank || 0) - (b.rank || 0)));
      setManualUrl('');
      setRefresh((value) => value + 1);
      notify({ tone: 'ok', text: '추천에 영상을 추가했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '영상 추가 실패' });
    }
  }

  // 쇼츠 추천 v1.0: [최신]/[인기] 탭은 서버가 저장한 item.tab으로 분리, 고정은 필터
  const byRank = (a: VideoResult, b: VideoResult) => (a.rank || 0) - (b.rank || 0);
  const visibleItems = sort === 'pinned'
    ? items.filter((item) => item.is_pinned).sort(byRank)
    : items.filter((item) => (item.tab || 'latest') === sort).sort(byRank);
  const shortage = sort !== 'pinned' && Boolean(selectedRun) && visibleItems.length > 0 && visibleItems.length < 7;

  async function togglePin(item: VideoResult) {
    if (!item.id) return;
    await api.updateRecommendationItem(item.id, { isPinned: !item.is_pinned });
    setItems((prev) => prev.map((row) => row.id === item.id ? { ...row, is_pinned: row.is_pinned ? 0 : 1 } : row));
  }

  async function sendToExample(item: VideoResult) {
    if (!item.id) return;
    await api.recommendationToExample(item.id);
    notify({ tone: 'ok', text: '예시에 등록했습니다' });
  }

  async function sendToArchive(item: VideoResult) {
    if (!item.id) return;
    await api.recommendationToArchive(item.id);
    notify({ tone: 'ok', text: '확정에 등록했습니다' });
  }

  async function saveRecommendationNote(item: VideoResult) {
    if (!item.id) return;
    const editorNote = noteDrafts[item.id] ?? item.editor_note ?? '';
    await api.updateRecommendationItem(item.id, { editorNote });
    setItems((current) => current.map((row) => row.id === item.id ? { ...row, editor_note: editorNote } : row));
    notify({ tone: 'ok', text: '추천 메모를 저장했습니다' });
  }

  function runBadge(run: RecommendationRun) {
    if (run.status === 'done') return <span className="sbadge s-done">완료</span>;
    if (run.status === 'failed') return <span className="sbadge s-failed">실패</span>;
    return <span className="sbadge s-pending">{run.status || '대기'}</span>;
  }

  return (
    <section className="panel active" id="panelRec">
      <div className="rec-toolbar">
        <div className="rec-toolbar-actions">
          <div className="rec-sort-toggle">
            <button className={`rec-sort-btn ${sort === 'latest' ? 'active' : ''}`} onClick={() => setSort('latest')}>최신</button>
            <button className={`rec-sort-btn ${sort === 'popular' ? 'active' : ''}`} onClick={() => setSort('popular')}>인기</button>
            <button className={`rec-sort-btn ${sort === 'pinned' ? 'active' : ''}`} onClick={() => setSort('pinned')} data-rec-sort="pinned">고정</button>
          </div>
          {shortage && <span className="warn-chip">후보 부족 {visibleItems.length}/7</span>}
          <div className="rec-toolbar-right">
            {role === 'admin' && (
              <>
                <input className="rank-input date" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
                <button className="btn-secondary btn-sm" onClick={() => generate(true)}>↺ 재추천</button>
                <button className="expl-collect-btn" onClick={() => generate(false)}>추천 생성</button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="rec-board">
        {runs.status === 'loading' && <EmptyPanel title="추천을 불러오는 중" />}
        {runs.status === 'ready' && !scopedRuns.length && (
          <EmptyPanel title={role === 'admin' && editorScope ? '이 편집자의 추천이 아직 없습니다' : '추천이 아직 없습니다'} />
        )}
        {scopedRuns.map((run) => {
          const open = selectedRun?.id === run.id;
          return (
            <section className={`rec-folder ${open ? 'open' : ''}`} key={run.id}>
              <button className="rec-folder-hdr" type="button" onClick={() => setSelectedRunId(open ? -1 : run.id)}>
                <div className="rec-folder-main">
                  <div>
                    <div className="rec-folder-name">{run.run_date}</div>
                    <div className="rec-folder-meta">
                      최신 최대 7 · 인기 최대 7 · {run.generated_by === 'cron' ? '자동 생성' : '수동 생성'}
                      {run.shortage_reason ? ` · 후보 부족 (${run.shortage_reason})` : ''}
                      {(run.regenerate_count || 0) > 0 ? ` · 재추천 ${run.regenerate_count}회` : ''}
                    </div>
                  </div>
                </div>
                <div className="rec-folder-right">
                  {runBadge(run)}
                  <span className="rec-folder-arrow">›</span>
                </div>
              </button>
              {open && (
                <div className="rec-folder-body">
                  {role === 'admin' && (
                    <div className="rec-folder-tools">
                      <input
                        className="rec-link-input"
                        type="url"
                        value={manualUrl}
                        onChange={(event) => setManualUrl(event.target.value)}
                        onKeyDown={(event) => { if (event.key === 'Enter') void addManualItem(); }}
                        placeholder="이 폴더에 링크 추가 · YouTube 쇼츠 링크"
                      />
                      <button className="btn-primary btn-sm" type="button" onClick={addManualItem}>+ 링크</button>
                    </div>
                  )}
                  {sort === 'pinned' && !visibleItems.length ? (
                    <div className="pin-empty-state">
                      <b>고정된게 없습니다.</b>
                      <span>고정버튼을 눌러 고정해주세요.</span>
                    </div>
                  ) : !visibleItems.length ? (
                    <div className="pin-empty-state">
                      <b>{sort === 'latest' ? '최신' : '인기'} 추천이 없습니다.</b>
                      <span>수집 후 추천 생성을 실행해주세요.</span>
                    </div>
                  ) : (
                    <div className="rec-vid-list">
                      {visibleItems.map((item, index) => (
                        <div className={`rec-vid-item ${item.is_pinned ? 'is-pinned' : ''}`} key={item.id || index}>
                          <div className={`rec-rank-num ${index === 0 ? 'n1' : index === 1 ? 'n2' : index === 2 ? 'n3' : ''}`}>{index + 1}</div>
                          <a className="rec-thumb" href={videoUrl(item)} target="_blank" rel="noreferrer">
                            {thumb(item) ? <img src={thumb(item)} alt="" /> : null}
                          </a>
                          <div className="rec-vid-info">
                            <a className="rec-vid-title" href={videoUrl(item)} target="_blank" rel="noreferrer">{item.title || '영상'}</a>
                            <a className="rec-vid-ch" href={channelUrl(item)} target="_blank" rel="noreferrer">{channelTitle(item)}</a>
                            <div className="rec-vid-stats">
                              <span className="c-blue">{formatNumber(viewCount(item))} 조회</span>
                              <span>좋아요 {formatNumber(item.like_count ?? item.likeCount)}</span>
                              <span>댓글 {formatNumber(item.comment_count ?? item.commentCount)}</span>
                            </div>
                            {role === 'admin' && <RecBadges item={item} />}
                            <div className="inline-note-row">
                              <input
                                className="rank-input grow"
                                value={item.id ? (noteDrafts[item.id] ?? item.editor_note ?? '') : ''}
                                onChange={(event) => item.id && setNoteDrafts((current) => ({ ...current, [item.id as number]: event.target.value }))}
                                placeholder="편집자 메모"
                              />
                              <button className="mini-btn" onClick={() => void saveRecommendationNote(item)}>메모 저장</button>
                            </div>
                          </div>
                          <div className="rec-item-actions">
                            <button className={`rec-pin-btn ${item.is_pinned ? 'on' : ''}`} onClick={() => void togglePin(item)}>{item.is_pinned ? '고정됨' : '고정'}</button>
                            <button className="action-route-btn dup" onClick={() => void sendToExample(item)}>예시</button>
                            <button className="action-route-btn arc" onClick={() => void sendToArchive(item)}>확정</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </section>
  );
}

function ExamplesView({ formType, editorScope, notify }: { formType: FormType; editorScope: string; notify: (notice: Notice) => void }) {
  const [sort, setSort] = useState<'latest' | 'popular' | 'pinned'>('latest');
  const [channelFilter, setChannelFilter] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [noteDrafts, setNoteDrafts] = useState<Record<number, string>>({});
  const examples = useApiData(`examples-${formType}-${editorScope}-${sort}-${refresh}`, () => api.examples(sort, formType, editorScope));
  const items = examples.data?.items || [];
  const channels = Array.from(new Set(items.map((item) => channelTitle(item)).filter(Boolean)));
  const visibleItems = channelFilter ? items.filter((item) => channelTitle(item) === channelFilter) : items;

  useEffect(() => {
    setNoteDrafts((current) => Object.fromEntries(items.filter((item) => item.id).map((item) => [
      item.id as number,
      current[item.id as number] ?? item.editor_note ?? '',
    ])));
  }, [examples.data]);

  async function togglePin(item: VideoResult) {
    if (!item.id) return;
    await api.updateExample(item.id, { isPinned: !item.is_pinned });
    setRefresh((value) => value + 1);
    notify({ tone: 'ok', text: item.is_pinned ? '고정을 해제했습니다' : '고정했습니다' });
  }

  async function sendToArchive(item: VideoResult) {
    if (!editorScope) {
      notify({ tone: 'error', text: '편집자를 먼저 선택해주세요' });
      return;
    }
    await api.addArchive({ ...videoArchivePayload(item), archived_for_email: editorScope, form_type: formType, category_key: 'all' });
    notify({ tone: 'ok', text: '확정에 등록했습니다' });
  }

  async function remove(item: VideoResult) {
    if (!item.id) return;
    await api.deleteExample(item.id);
    setRefresh((value) => value + 1);
    notify({ tone: 'ok', text: '예시에서 삭제했습니다' });
  }

  async function saveExampleNote(item: VideoResult) {
    if (!item.id) return;
    const editorNote = noteDrafts[item.id] ?? item.editor_note ?? '';
    await api.updateExample(item.id, { editorNote });
    setRefresh((value) => value + 1);
    notify({ tone: 'ok', text: '예시 메모를 저장했습니다' });
  }

  return (
    <section className="panel active" id="panelDup" data-ex-view="list">
      <SubTabs tabs={[['latest', '최신순'], ['popular', '인기순'], ['pinned', '고정']]} active={sort} onChange={(key) => setSort(key as typeof sort)} />
      <div className="content">
        {channels.length > 1 && (
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <button className={`rank-chip ${!channelFilter ? 'on' : ''}`} onClick={() => setChannelFilter('')}>전체</button>
            {channels.map((name) => (
              <button key={name} className={`rank-chip ${channelFilter === name ? 'on' : ''}`} onClick={() => setChannelFilter(name)}>{name}</button>
            ))}
          </div>
        )}
        {examples.status === 'loading' && <EmptyPanel title="예시를 불러오는 중" />}
        {examples.status === 'ready' && sort === 'pinned' && !visibleItems.length && (
          <EmptyPanel title="고정된게 없습니다. 고정버튼을 눌러 고정해주세요." />
        )}
        {examples.status === 'ready' && sort !== 'pinned' && !visibleItems.length && (
          <EmptyPanel title="예시 영상이 없습니다" />
        )}
        {visibleItems.length > 0 && (
          <div className="ex-list">
            {visibleItems.map((item) => (
              <div className="ex-vid-row" key={item.id}>
                <a className="ex-thumb" href={videoUrl(item)} target="_blank" rel="noreferrer">
                  {thumb(item) ? <img src={thumb(item)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : null}
                </a>
                <div className="ex-info">
                  <a className="ex-title" href={videoUrl(item)} target="_blank" rel="noreferrer" style={{ display: 'block' }}>{item.title || '영상'}</a>
                  <a className="ex-ch" href={channelUrl(item)} target="_blank" rel="noreferrer" style={{ display: 'block' }}>{channelTitle(item)}</a>
                  <div className="ex-stats">
                    <span className="c-blue">{formatNumber(viewCount(item))} 조회</span>
                    <span>{formatDate(item.publishedAt || item.published_at)}</span>
                  </div>
                  <div className="inline-note-row">
                    <input
                      className="rank-input grow"
                      value={item.id ? (noteDrafts[item.id] ?? item.editor_note ?? '') : ''}
                      onChange={(event) => item.id && setNoteDrafts((current) => ({ ...current, [item.id as number]: event.target.value }))}
                      placeholder="편집자 메모"
                    />
                    <button className="mini-btn" onClick={() => void saveExampleNote(item)}>메모 저장</button>
                  </div>
                  <div className="ex-acts">
                    <button className={`ex-pin-btn ${item.is_pinned ? 'on' : ''}`} onClick={() => void togglePin(item)}>{item.is_pinned ? '고정됨' : '고정'}</button>
                    <button className="action-route-btn arc" onClick={() => void sendToArchive(item)}>확정</button>
                    <button className="mini-btn danger" onClick={() => void remove(item)}>삭제</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ArchiveView({ formType, editorScope, notify }: { formType: FormType; editorScope: string; notify: (notice: Notice) => void }) {
  const [status, setStatus] = useState('');
  const [refresh, setRefresh] = useState(0);
  const [eventsById, setEventsById] = useState<Record<number, ArchiveEvent[]>>({});
  const [noteDrafts, setNoteDrafts] = useState<Record<number, string>>({});
  const archive = useApiData(`archive-${formType}-${editorScope}-${status}-${refresh}`, () => api.archive({ ...(status ? { status } : {}), formType, editorEmail: editorScope }));
  const items = archive.data?.items || [];
  const itemIdsKey = items.map((item) => item.id).filter(Boolean).join(',');

  useEffect(() => {
    setNoteDrafts((prev) => {
      const next = { ...prev };
      for (const item of items) {
        if (!item.id) continue;
        if (next[item.id] === undefined) next[item.id] = item.editor_note || '';
      }
      return next;
    });
  }, [itemIdsKey]);

  useEffect(() => {
    let active = true;
    const ids = items.map((item) => item.id).filter((id): id is number => Number.isFinite(id));
    if (!ids.length) {
      setEventsById({});
      return () => { active = false; };
    }
    Promise.all(ids.map(async (id) => {
      try {
        const result = await api.archiveEvents(id);
        return [id, result.events] as const;
      } catch {
        return [id, []] as const;
      }
    })).then((pairs) => {
      if (!active) return;
      setEventsById(Object.fromEntries(pairs));
    });
    return () => { active = false; };
  }, [itemIdsKey, refresh]);

  async function updateStatus(item: VideoResult, nextStatus: string) {
    if (!item.id) return;
    await api.updateArchive(item.id, { status: nextStatus });
    setRefresh((value) => value + 1);
    notify({ tone: 'ok', text: '상태를 저장했습니다' });
  }

  async function saveNote(item: VideoResult) {
    if (!item.id) return;
    await api.updateArchive(item.id, { editor_note: noteDrafts[item.id] || '' });
    setRefresh((value) => value + 1);
    notify({ tone: 'ok', text: '메모를 저장했습니다' });
  }

  async function unarchive(item: VideoResult) {
    if (!item.id) return;
    if (!window.confirm('정말로 확정 해제하시겠어요?')) return;
    await api.deleteArchive(item.id);
    setRefresh((value) => value + 1);
    notify({ tone: 'ok', text: '확정을 해제했습니다' });
  }

  const ARCHIVE_STATUSES: Array<[string, string]> = [
    ['editing', '편집 중'],
    ['completed', '편집 완료'],
    ['revision_requested', '수정 요청'],
    ['revising', '수정 중'],
  ];

  return (
    <section className="panel active">
      <div className="sub-tabs">
        {[['', '전체'], ...ARCHIVE_STATUSES].map(([key, label]) => (
          <button key={key} className={`sub-tab ${status === key ? 'active' : ''}`} onClick={() => setStatus(key)}>{label}</button>
        ))}
        <div className="sub-r"><span className="sec-meta">{items.length}개</span></div>
      </div>
      <div className="content">
        {archive.status === 'loading' && <EmptyPanel title="확정을 불러오는 중" />}
        {archive.status === 'ready' && !items.length && <EmptyPanel title="확정된 영상이 없습니다" />}
        <div className="arc-dynamic-list">
          {items.map((item) => {
            const events = (item.id && eventsById[item.id]) || [];
            const badgeClass = item.status === 'completed' ? 's-done' : item.status === 'revision_requested' ? 's-purple' : 's-editing';
            return (
              <div className="arc-card" key={item.id}>
                <button className="arc-corner-unarchive" onClick={() => void unarchive(item)}>확정 해제</button>
                <div className="arc-top">
                  <a className="arc-thumb" href={videoUrl(item)} target="_blank" rel="noreferrer">
                    {thumb(item) ? <img src={thumb(item)} alt="" /> : null}
                  </a>
                  <div className="arc-info">
                    <span className={`sbadge ${badgeClass}`}>{archiveStatusLabel(item.status)}</span>
                    <a className="arc-title" href={videoUrl(item)} target="_blank" rel="noreferrer" style={{ display: 'block', marginTop: '5px' }}>{item.title || '영상'}</a>
                    <a className="arc-ch" href={channelUrl(item)} target="_blank" rel="noreferrer">{channelTitle(item)}</a>
                    <div className="arc-stats">
                      <span>조회수 {formatNumber(viewCount(item))}</span>
                      <span>댓글수 {formatNumber(item.comment_count ?? item.commentCount)}</span>
                      <span>좋아요수 {formatNumber(item.like_count ?? item.likeCount)}</span>
                    </div>
                    <div className="arc-history">
                      {item.archived_at && <span className="arc-log">확정 {formatUnixTime(item.archived_at)}</span>}
                      {events.slice(-3).map((event) => (
                        <span className="arc-log" key={event.id}>{archiveEventLabel(event)} {formatUnixTime(event.created_at)}</span>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="arc-edit">
                  <select
                    className="status-sel"
                    value={item.status || 'editing'}
                    onChange={(event) => void updateStatus(item, event.target.value)}
                  >
                    {ARCHIVE_STATUSES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                  </select>
                  <textarea
                    className="note-inp"
                    placeholder="작업 메모"
                    value={(item.id != null ? noteDrafts[item.id] : undefined) ?? item.editor_note ?? ''}
                    onChange={(event) => {
                      if (item.id == null) return;
                      setNoteDrafts((prev) => ({ ...prev, [item.id as number]: event.target.value }));
                    }}
                  />
                  <button className="btn-save" onClick={() => void saveNote(item)}>저장</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function GuidelinesView({ role, editorScope, notify }: { role: Role; editorScope: string; notify: (notice: Notice) => void }) {
  const [section, setSection] = useState<'steps' | 'tools'>('steps');
  const [refresh, setRefresh] = useState(0);
  const guidelines = useApiData(`guidelines-${editorScope}-${refresh}`, () => api.guidelines(editorScope));
  const steps = guidelines.data?.steps || [];
  const folders = guidelines.data?.toolFolders || [];
  const reload = () => setRefresh((value) => value + 1);
  async function toggleProgress(step: GuidelineStep) {
    try {
      await api.updateGuidelineProgress(step.id, {
        isCompleted: Number(step.is_completed || 0) !== 1,
        editorEmail: editorScope,
      });
      reload();
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '진행 상태 저장 실패' });
    }
  }

  return (
    <section className="panel active" id="panelTools">
      <SubTabs tabs={[['steps', '편집 순서'], ['tools', '도구 모음']]} active={section} onChange={(key) => setSection(key as typeof section)} />
      <div className="guide-stage">
        {guidelines.status === 'loading' || guidelines.status === 'error' ? (
          <DataState state={guidelines} emptyTitle="지침이 없습니다" />
        ) : section === 'steps' ? (
          <>
            <div className="step-flow">
              {steps.map((step, index) => (
                <StepNode key={step.id} step={step} index={index} onToggle={() => void toggleProgress(step)} />
              ))}
            </div>
            {role === 'admin' && (
              <GuidelineStepAdmin
                steps={steps}
                onSaved={reload}
                notify={notify}
              />
            )}
          </>
        ) : (
          <>
            <div className="tool-folder-row">
              {folders.map((folder) => <ToolFolder key={folder.id} folder={folder} />)}
            </div>
            {role === 'admin' && (
              <GuidelineToolAdmin
                folders={folders}
                onSaved={reload}
                notify={notify}
              />
            )}
          </>
        )}
      </div>
    </section>
  );
}

function SimilarityPanel({
  state,
  onBack,
  onRegisterChannel,
  onArchiveVideo,
  onSimilarChannel,
  onSimilarVideo,
}: {
  state: SimilarState;
  onBack: () => void;
  onRegisterChannel?: (target: AssetTarget, channel: ChannelResult) => void | Promise<void>;
  onArchiveVideo?: (item: VideoResult) => void | Promise<void>;
  onSimilarChannel?: (item: ChannelResult | VideoResult) => void | Promise<void>;
  onSimilarVideo?: (item: VideoResult) => void | Promise<void>;
}) {
  const sourceTitle = state.kind === 'channel'
    ? resultChannelTitle(state.source)
    : state.result?.sourceVideo?.title || state.source.title || '기준 영상';
  const sourceMeta = state.kind === 'channel'
    ? `채널 ID ${resultChannelId(state.source) || '-'}`
    : `조회수 ${formatNumber(viewCount(state.result?.sourceVideo || state.source))} · 댓글수 ${formatNumber((state.result?.sourceVideo || state.source).commentCount ?? (state.result?.sourceVideo || state.source).comment_count)} · 좋아요수 ${formatNumber((state.result?.sourceVideo || state.source).likeCount ?? (state.result?.sourceVideo || state.source).like_count)} · 업로드일자 ${formatDate((state.result?.sourceVideo || state.source).publishedAt || (state.result?.sourceVideo || state.source).published_at)}`;

  return (
    <section className="panel active">
      <div className="content">
        <div className="similar-head">
          <button className="btn-secondary" onClick={onBack}>← 뒤로가기</button>
          <div className="similar-title">
            <strong>{state.kind === 'channel' ? '유사 채널 찾기' : '유사 영상 찾기'}</strong>
            <span>{sourceTitle}</span>
          </div>
        </div>
        <div className="similar-source">
          {state.kind === 'video' && thumb(state.result?.sourceVideo || state.source) ? (
            <a className="mini-thumb" href={videoUrl(state.result?.sourceVideo || state.source)} target="_blank" rel="noreferrer">
              <img src={thumb(state.result?.sourceVideo || state.source)} alt="" />
            </a>
          ) : state.kind === 'channel' && (state.result?.sourceChannel?.thumbnail || (state.source as ChannelResult).thumbnail) ? (
            <span className="channel-avatar sm">
              <img src={state.result?.sourceChannel?.thumbnail || (state.source as ChannelResult).thumbnail || ''} alt="" />
            </span>
          ) : null}
          <div>
            <div className="similar-source-title">{sourceTitle}</div>
            <div className="similar-source-meta">{sourceMeta}</div>
            {state.result?.query && <div className="similar-source-meta">검색 기준 · {state.result.query}</div>}
          </div>
        </div>
        {state.status === 'loading' && <EmptyPanel title="유사 결과를 불러오는 중" />}
        {state.status === 'error' && <ErrorBox text={state.error} />}
        {state.status === 'ready' && state.kind === 'channel' && (
          <ChannelResults
            items={(state.result?.items || []) as ChannelResult[]}
            view="card"
            onRegister={onRegisterChannel || (() => undefined)}
            onSimilar={onSimilarChannel}
          />
        )}
        {state.status === 'ready' && state.kind === 'video' && (
          <VideoResults
            items={(state.result?.items || []) as VideoResult[]}
            view="table"
            mode="explore"
            emptyTitle="유사 영상이 없습니다"
            onArchive={onArchiveVideo}
            onSimilarChannel={onSimilarChannel}
            onSimilarVideo={onSimilarVideo}
          />
        )}
      </div>
    </section>
  );
}

function ChannelResults({
  items,
  view,
  onRegister,
  onSimilar,
}: {
  items: ChannelResult[];
  view: 'card' | 'table';
  onRegister: (target: AssetTarget, channel: ChannelResult) => void;
  onSimilar?: (channel: ChannelResult) => void | Promise<void>;
}) {
  if (!items.length) return <EmptyPanel title="채널 결과가 없습니다" />;
  if (view === 'card') {
    return (
      <div className="channel-card-list">
        {items.map((channel) => (
          <div className="channel-card" key={channel.channelId}>
            <div className="rank-badge">#{channel.rank || '-'}</div>
            <a className="channel-avatar" href={channel.youtubeUrl} target="_blank" rel="noreferrer">
              {channel.thumbnail ? <img src={channel.thumbnail} alt="" /> : <span>📡</span>}
            </a>
            <div className="channel-main">
              <a className="channel-title" href={channel.youtubeUrl} target="_blank" rel="noreferrer">{channel.channelTitle || channel.title || channel.channelId}</a>
              <div className="channel-meta">
                구독자 {formatNumber(channel.subscriberCount)} · 총조회수 {formatNumber(channel.totalViewCount)} · 영상 {formatNumber(channel.videoCount)}
              </div>
              <RecentVideoStrip videos={channel.recentVideos || []} />
            </div>
            <div className="row-actions">
              {onSimilar && <button className="mini-btn" onClick={() => onSimilar(channel)}>유사 채널</button>}
              <button className="mini-btn green" onClick={() => onRegister('recommendation', channel)}>추천</button>
              <button className="mini-btn purple" onClick={() => onRegister('example', channel)}>예시</button>
              <a className="mini-btn" href={channel.youtubeUrl} target="_blank" rel="noreferrer">채널</a>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>순위</th>
            <th>채널 정보</th>
            <th>구독자</th>
            <th>총조회수</th>
            <th>영상수</th>
            <th>최근 영상</th>
            <th>에셋에 등록</th>
          </tr>
        </thead>
        <tbody>
          {items.map((channel) => (
            <tr key={channel.channelId}>
              <td>#{channel.rank || '-'}</td>
              <td>
                <a className="inline-media" href={channel.youtubeUrl} target="_blank" rel="noreferrer">
                  {channel.thumbnail ? <img src={channel.thumbnail} alt="" /> : <span>📡</span>}
                  <span>{channel.channelTitle || channel.title || channel.channelId}</span>
                </a>
              </td>
              <td>{formatNumber(channel.subscriberCount)}</td>
              <td>{formatNumber(channel.totalViewCount)}</td>
              <td>{formatNumber(channel.videoCount)}</td>
              <td><RecentVideoStrip videos={channel.recentVideos || []} compact /></td>
              <td>
                <div className="row-actions">
                  {onSimilar && <button className="mini-btn" onClick={() => onSimilar(channel)}>유사 채널</button>}
                  <button className="mini-btn green" onClick={() => onRegister('recommendation', channel)}>추천</button>
                  <button className="mini-btn purple" onClick={() => onRegister('example', channel)}>예시</button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// 쇼츠 추천 v1.0 뱃지/원값 표시: 창 뱃지(48h/7d/28d/12m/24m/36m), 결측/자료부족 뱃지,
// 원값(점수는 백분위 기반이지만 표시는 원값: 크랙 ×N, 참여율 N/만뷰, VPH, HER)
function RecBadges({ item }: { item: VideoResult }) {
  if (!item.tab) return null;
  let detail: Record<string, unknown> = {};
  let missing: string[] = [];
  try { detail = JSON.parse(item.score_detail_json || '{}'); } catch { detail = {}; }
  try { missing = JSON.parse(item.missing_badges_json || '[]'); } catch { missing = []; }
  const num = (value: unknown) => (typeof value === 'number' && Number.isFinite(value) ? value : null);
  const viewCrack = num(detail.viewCrack);
  const engCrack = num(detail.engCrack);
  const engagement = num(detail.engagement);
  const vph = num(detail.vph);
  const her = num(detail.her);
  return (
    <div className="rec-badges">
      {item.window_used && <span className="win-chip">{item.window_used}</span>}
      {typeof item.final_score === 'number' && item.final_score > 0 && (
        <span className="rec-final">{item.final_score.toFixed(1)}점</span>
      )}
      <span className="rec-raws">
        {viewCrack != null && <em>크랙 ×{viewCrack.toFixed(1)}</em>}
        {engCrack != null && <em>참여크랙 ×{engCrack.toFixed(1)}</em>}
        {engagement != null && <em>참여율 {Math.round(engagement)}/만뷰</em>}
        {vph != null && <em>VPH {formatNumber(Math.round(vph))}/시간</em>}
        {her != null && <em>HER {formatNumber(Math.round(her))}/시간</em>}
      </span>
      {missing.map((badge) => <span className="warn-chip sm" key={badge}>{badge}</span>)}
    </div>
  );
}

function VideoResults({
  items,
  view,
  mode,
  emptyTitle = '영상이 없습니다',
  onPin,
  onRecommendation,
  onExample,
  onArchive,
  onDelete,
  onStatus,
  onSimilarChannel,
  onSimilarVideo,
  archiveEvents,
  noteDrafts,
  onNoteChange,
  onNoteSave,
}: {
  items: VideoResult[];
  view: 'card' | 'table';
  mode: 'explore' | 'collection' | 'recommendation' | 'example' | 'archive';
  emptyTitle?: string;
  onPin?: (item: VideoResult) => void | Promise<void>;
  onRecommendation?: (item: VideoResult) => void | Promise<void>;
  onExample?: (item: VideoResult) => void | Promise<void>;
  onArchive?: (item: VideoResult) => void | Promise<void>;
  onDelete?: (item: VideoResult) => void | Promise<void>;
  onStatus?: (item: VideoResult, status: string) => void | Promise<void>;
  onSimilarChannel?: (item: VideoResult) => void | Promise<void>;
  onSimilarVideo?: (item: VideoResult) => void | Promise<void>;
  archiveEvents?: Record<number, ArchiveEvent[]>;
  noteDrafts?: Record<number, string>;
  onNoteChange?: (item: VideoResult, value: string) => void;
  onNoteSave?: (item: VideoResult) => void | Promise<void>;
}) {
  const showInternalMetrics = mode === 'explore' || mode === 'collection';
  if (!items.length) return <EmptyPanel title={emptyTitle} />;
  if (view === 'card') {
    return (
      <div className="video-grid">
        {items.map((item, index) => (
          <article className="video-card" key={videoId(item) || item.id || index}>
            <a className="video-thumb" href={videoUrl(item)} target="_blank" rel="noreferrer">
              {thumb(item) ? <img src={thumb(item)} alt="" /> : null}
              {showInternalMetrics && <span className="score-badge">★ {formatScore(item)}</span>}
            </a>
            <a className="video-title" href={videoUrl(item)} target="_blank" rel="noreferrer">{item.title || '영상'}</a>
            <a className="video-channel" href={channelUrl(item)} target="_blank" rel="noreferrer">{channelTitle(item)}</a>
            {mode === 'collection' && <CollectionSourceMeta item={item} />}
            {mode === 'recommendation' && <RecBadges item={item} />}
            <MetricStrip item={item} />
            <VideoActions
              mode={mode}
              item={item}
              onPin={onPin}
              onRecommendation={onRecommendation}
              onExample={onExample}
              onArchive={onArchive}
              onDelete={onDelete}
              onStatus={onStatus}
              onSimilarChannel={onSimilarChannel}
              onSimilarVideo={onSimilarVideo}
              events={item.id ? archiveEvents?.[item.id] : undefined}
              noteValue={item.id ? noteDrafts?.[item.id] : undefined}
              onNoteChange={onNoteChange}
              onNoteSave={onNoteSave}
            />
          </article>
        ))}
      </div>
    );
  }
  return (
    <div className="table-wrap">
      <table className="data-table video">
        <thead>
          <tr>
            <th>순위</th>
            <th>미리보기</th>
            <th>제목 / 채널</th>
            {showInternalMetrics && <th>종합점수</th>}
            <th>조회수</th>
            {showInternalMetrics && <th>아웃라이어</th>}
            {showInternalMetrics && <th>참여율</th>}
            {showInternalMetrics && <th>HER</th>}
            {showInternalMetrics && <th>VPH</th>}
            <th>좋아요수</th>
            <th>댓글수</th>
            {showInternalMetrics && <th>구독자수</th>}
            <th>업로드날짜</th>
            <th>저장</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => (
            <tr key={videoId(item) || item.id || index}>
              <td>#{item.rank || index + 1}</td>
              <td>
                <a className="mini-thumb" href={videoUrl(item)} target="_blank" rel="noreferrer">
                  {thumb(item) ? <img src={thumb(item)} alt="" /> : null}
                </a>
              </td>
              <td>
                <a className="row-title" href={videoUrl(item)} target="_blank" rel="noreferrer">{item.title || '영상'}</a>
                <a className="row-sub" href={channelUrl(item)} target="_blank" rel="noreferrer">{channelTitle(item)}</a>
                {mode === 'collection' && <CollectionSourceMeta item={item} />}
                {mode === 'recommendation' && <RecBadges item={item} />}
              </td>
              {showInternalMetrics && <td><span className="score-pill">★ {formatScore(item)}</span></td>}
              <td className="num blue">{formatNumber(viewCount(item))}</td>
              {showInternalMetrics && <td className="num gold">{formatMetric(item.outlierScore ?? item.outlier_score ?? item.outlier)}</td>}
              {showInternalMetrics && <td>{formatMetric(item.engagementRate ?? item.engagement_rate)}%</td>}
              {showInternalMetrics && <td>{formatMetric(item.her)}</td>}
              {showInternalMetrics && <td className="num cyan">{formatNumber(item.vph)}</td>}
              <td>{formatNumber(item.likeCount ?? item.like_count)}</td>
              <td>{formatNumber(item.commentCount ?? item.comment_count)}</td>
              {showInternalMetrics && <td>{formatNumber(item.subscriberCount ?? item.subscriber_count)}</td>}
              <td>{formatDate(item.publishedAt || item.published_at)}</td>
              <td>
                <VideoActions
                  mode={mode}
                  item={item}
                  onPin={onPin}
                  onRecommendation={onRecommendation}
                  onExample={onExample}
                  onArchive={onArchive}
                  onDelete={onDelete}
                  onStatus={onStatus}
                  onSimilarChannel={onSimilarChannel}
                  onSimilarVideo={onSimilarVideo}
                  events={item.id ? archiveEvents?.[item.id] : undefined}
                  noteValue={item.id ? noteDrafts?.[item.id] : undefined}
                  onNoteChange={onNoteChange}
                  onNoteSave={onNoteSave}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CollectionSourceMeta({ item }: { item: VideoResult }) {
  const sources = item.sources || [];
  const uniqueLabels = [...new Set(sources.map((source) => source.condition_label || source.source).filter(Boolean))];
  return (
    <div className="collection-source-meta">
      {uniqueLabels.slice(0, 3).map((label) => <span key={label}>{label}</span>)}
      {uniqueLabels.length > 3 && <span>+{uniqueLabels.length - 3}</span>}
      {item.last_collected_at ? <em>수집 {formatUnixTime(item.last_collected_at)}</em> : null}
      {item.shorts_check_status && item.shorts_check_status !== 'confirmed' ? (
        <em className="warn">{item.shorts_check_status}{item.shorts_check_error ? ` · ${item.shorts_check_error}` : ''}</em>
      ) : null}
    </div>
  );
}

function VideoActions({
  mode,
  item,
  onPin,
  onRecommendation,
  onExample,
  onArchive,
  onDelete,
  onStatus,
  onSimilarChannel,
  onSimilarVideo,
  events,
  noteValue,
  onNoteChange,
  onNoteSave,
}: {
  mode: string;
  item: VideoResult;
  onPin?: (item: VideoResult) => void | Promise<void>;
  onRecommendation?: (item: VideoResult) => void | Promise<void>;
  onExample?: (item: VideoResult) => void | Promise<void>;
  onArchive?: (item: VideoResult) => void | Promise<void>;
  onDelete?: (item: VideoResult) => void | Promise<void>;
  onStatus?: (item: VideoResult, status: string) => void | Promise<void>;
  onSimilarChannel?: (item: VideoResult) => void | Promise<void>;
  onSimilarVideo?: (item: VideoResult) => void | Promise<void>;
  events?: ArchiveEvent[];
  noteValue?: string;
  onNoteChange?: (item: VideoResult, value: string) => void;
  onNoteSave?: (item: VideoResult) => void | Promise<void>;
}) {
  return (
    <div className={mode === 'archive' ? 'archive-tools' : 'row-actions'}>
      <div className="row-actions">
        {onSimilarChannel && <button className="mini-btn" onClick={() => onSimilarChannel(item)}>유사 채널</button>}
        {onSimilarVideo && <button className="mini-btn" onClick={() => onSimilarVideo(item)}>유사 영상</button>}
        {onRecommendation && <button className="mini-btn green" onClick={() => onRecommendation(item)}>추천</button>}
        {onExample && <button className="mini-btn purple" onClick={() => onExample(item)}>예시</button>}
        {onArchive && <button className="mini-btn brown" onClick={() => onArchive(item)}>확정</button>}
        {onPin && <button className={`mini-btn ${item.is_pinned ? 'purple active' : ''}`} onClick={() => onPin(item)}>{item.is_pinned ? '고정됨' : '고정'}</button>}
        {mode === 'archive' && onStatus && (
          <>
            <button className={`mini-btn ${item.status === 'editing' ? 'green active' : ''}`} onClick={() => onStatus(item, 'editing')}>편집 중</button>
            <button className={`mini-btn ${item.status === 'completed' ? 'green active' : ''}`} onClick={() => onStatus(item, 'completed')}>편집 완료</button>
            <button className={`mini-btn ${item.status === 'revision_requested' ? 'green active' : ''}`} onClick={() => onStatus(item, 'revision_requested')}>수정 요청</button>
            <button className={`mini-btn ${item.status === 'revising' ? 'green active' : ''}`} onClick={() => onStatus(item, 'revising')}>수정 중</button>
          </>
        )}
        {onDelete && <button className="mini-btn danger" onClick={() => onDelete(item)}>{mode === 'archive' ? '확정 해제' : '삭제'}</button>}
      </div>
      {mode === 'archive' && (
        <>
          <div className="archive-note-row">
            <textarea
              className="archive-note"
              value={noteValue ?? item.editor_note ?? ''}
              onChange={(event) => onNoteChange?.(item, event.target.value)}
              placeholder="작업 메모"
            />
            <button className="mini-btn green" onClick={() => onNoteSave?.(item)}>저장</button>
          </div>
          <ArchiveHistory item={item} events={events || []} />
        </>
      )}
    </div>
  );
}

function ArchiveHistory({ item, events }: { item: VideoResult; events: ArchiveEvent[] }) {
  const lines = [
    item.archived_at ? `확정 ${formatUnixTime(item.archived_at)}` : '',
    ...events.slice(-3).map((event) => `${archiveEventLabel(event)} ${formatUnixTime(event.created_at)}`),
  ].filter(Boolean);
  if (!lines.length) return null;
  return (
    <div className="archive-history">
      {lines.map((line, index) => <span key={`${line}-${index}`}>{line}</span>)}
    </div>
  );
}

function SettingsPanel({
  devEmail,
  setDevEmail,
  role,
  notify,
  onClose,
}: {
  devEmail: string;
  setDevEmail: (email: string) => void;
  role: Role;
  notify: (notice: Notice) => void;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(devEmail);
  const [keys, setKeys] = useState<Array<{ id: number; label: string; status: string; maskedKey: string; storage: string }>>([]);
  const [keyDraft, setKeyDraft] = useState('');
  const [keyLabel, setKeyLabel] = useState('YouTube Data API');
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [settingsCategories, setSettingsCategories] = useState<Category[]>([]);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserRole, setNewUserRole] = useState<Role>('editor');
  const [permissionUserId, setPermissionUserId] = useState('');
  const [permissionCategory, setPermissionCategory] = useState('all');
  const [permissionForm, setPermissionForm] = useState<FormType>('shorts');
  const [adminUrl, setAdminUrl] = useState('/admin');
  const [editorUrl, setEditorUrl] = useState('/editor');
  const [geminiEnabled, setGeminiEnabled] = useState(false);

  useEffect(() => {
    if (role !== 'admin') return;
    let active = true;
    Promise.allSettled([api.keys(), api.users(), api.categories(), api.settings()])
      .then(([keyResult, userResult, categoryResult, settingsResult]) => {
        if (!active) return;
        setKeys(keyResult.status === 'fulfilled' ? keyResult.value : []);
        setUsers(userResult.status === 'fulfilled' ? userResult.value.users : []);
        setSettingsCategories(categoryResult.status === 'fulfilled' ? categoryResult.value.categories : []);
        if (settingsResult.status === 'fulfilled') {
          const settings = settingsResult.value.settings || {};
          const uiUrls = (settings['ui.urls'] || {}) as { admin?: string; editor?: string };
          const gemini = settings['gemini.enabled'] as boolean | { enabled?: boolean } | undefined;
          setAdminUrl(uiUrls.admin || '/admin');
          setEditorUrl(uiUrls.editor || '/editor');
          setGeminiEnabled(typeof gemini === 'boolean' ? gemini : Boolean(gemini?.enabled));
        }
      });
    return () => { active = false; };
  }, [role]);

  useEffect(() => {
    if (permissionUserId || !users.length) return;
    const firstEditor = users.find((user) => user.role === 'editor') || users[0];
    if (firstEditor) setPermissionUserId(String(firstEditor.id));
  }, [permissionUserId, users]);

  async function reloadUsers() {
    const data = await api.users();
    setUsers(data.users);
  }

  async function saveKey() {
    try {
      const saved = await api.createKey({ key: keyDraft.trim(), label: keyLabel.trim() || 'YouTube Data API' });
      setKeys((rows) => [saved, ...rows.filter((row) => row.id !== saved.id)]);
      setKeyDraft('');
      notify({ tone: 'ok', text: 'API 키를 암호화해서 저장했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : 'API 키 저장 실패' });
    }
  }

  async function saveAppSettings() {
    try {
      await Promise.all([
        api.updateSetting('ui.urls', { admin: adminUrl.trim() || '/admin', editor: editorUrl.trim() || '/editor' }),
        api.updateSetting('gemini.enabled', { enabled: geminiEnabled }),
      ]);
      notify({ tone: 'ok', text: '설정을 저장했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '설정 저장 실패' });
    }
  }

  async function createUser() {
    try {
      await api.createUser({ email: newUserEmail.trim(), displayName: newUserName.trim(), role: newUserRole });
      setNewUserEmail('');
      setNewUserName('');
      await reloadUsers();
      notify({ tone: 'ok', text: '사용자를 추가했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '사용자 추가 실패' });
    }
  }

  async function toggleUser(user: UserRecord) {
    try {
      await api.updateUser(user.id, { isActive: !user.is_active });
      await reloadUsers();
      notify({ tone: 'ok', text: user.is_active ? '사용자를 비활성화했습니다' : '사용자를 활성화했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '사용자 수정 실패' });
    }
  }

  async function addPermission() {
    const userId = Number(permissionUserId);
    if (!Number.isFinite(userId) || userId <= 0) {
      notify({ tone: 'error', text: '권한을 추가할 사용자를 선택해주세요' });
      return;
    }
    try {
      await api.addUserPermission(userId, { categoryKey: permissionCategory, formType: permissionForm });
      await reloadUsers();
      notify({ tone: 'ok', text: '권한을 추가했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '권한 추가 실패' });
    }
  }

  async function removePermission(user: UserRecord, permissionId?: number) {
    if (!permissionId) return;
    try {
      await api.deleteUserPermission(user.id, permissionId);
      await reloadUsers();
      notify({ tone: 'ok', text: '권한을 해제했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '권한 해제 실패' });
    }
  }

  return (
    <aside className="sp on">
      <div className="sp-hdr">
        <div className="sp-title">⚙ 설정</div>
        <button className="sp-close" onClick={onClose}>✕</button>
      </div>
      <div className="sp-body">
        <div className="sp-lbl">Local Debug Email</div>
        <div className="sp-row">
          <input className="sp-inp" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="artubeditor@gmail.com" />
          <button
            className="btn-secondary btn-sm"
            onClick={() => {
              const next = draft.trim();
              if (next) localStorage.setItem('pick7.devEmail', next);
              else localStorage.removeItem('pick7.devEmail');
              setDevEmail(next);
            }}
          >
            적용
          </button>
        </div>
        <div className="sp-sep" />
        <div className="sp-lbl">API</div>
        <div className="sp-hint">{import.meta.env.VITE_PICK7_API_BASE || 'https://pick7-worker.artubeditor.workers.dev'}</div>
        {role === 'admin' && (
          <>
            <div className="sp-sep" />
            <div className="sp-lbl">YouTube Data API</div>
            <div className="sp-key-status">
              {keys.length ? keys.map((key) => (
                <div className="sp-key-row" key={key.id}>
                  <div className="sp-key-left">
                    <span className="sp-kdot on" />
                    <span className="sp-key-lbl">{key.label}</span>
                  </div>
                  <span className="sp-key-count active">{key.maskedKey} · {key.storage}</span>
                </div>
              )) : (
                <div className="sp-key-row">
                  <div className="sp-key-left">
                    <span className="sp-kdot" />
                    <span className="sp-key-lbl">등록된 키 없음</span>
                  </div>
                </div>
              )}
            </div>
            <input className="sp-inp" value={keyLabel} onChange={(event) => setKeyLabel(event.target.value)} placeholder="라벨" />
            <textarea
              className="sp-textarea"
              value={keyDraft}
              onChange={(event) => setKeyDraft(event.target.value)}
              placeholder="YouTube API 키 원문은 저장 후 다시 표시하지 않습니다"
            />
            <button className="btn-primary" onClick={saveKey} disabled={!keyDraft.trim()}>키 저장</button>
            <div className="sp-sep" />
            <div className="sp-lbl">앱 URL</div>
            <input className="sp-inp" value={adminUrl} onChange={(event) => setAdminUrl(event.target.value)} placeholder="/admin" />
            <input className="sp-inp" value={editorUrl} onChange={(event) => setEditorUrl(event.target.value)} placeholder="/editor" />
            <label className="sp-check-row">
              <input type="checkbox" checked={geminiEnabled} onChange={(event) => setGeminiEnabled(event.target.checked)} />
              <span>Gemini 추천</span>
            </label>
            <button className="btn-secondary btn-sm" onClick={saveAppSettings}>설정 저장</button>
            <div className="sp-sep" />
            <div className="sp-lbl">편집자 권한</div>
            <div className="sp-row">
              <input
                className="sp-inp"
                value={newUserName}
                onChange={(event) => setNewUserName(event.target.value)}
                placeholder="편집자 이름"
              />
              <input
                className="sp-inp"
                value={newUserEmail}
                onChange={(event) => setNewUserEmail(event.target.value)}
                placeholder="editor@example.com"
              />
              <select className="sp-inp" value={newUserRole} onChange={(event) => setNewUserRole(event.target.value as Role)}>
                <option value="editor">editor</option>
                <option value="admin">admin</option>
              </select>
              <button className="btn-secondary btn-sm" onClick={createUser} disabled={!newUserEmail.trim()}>추가</button>
            </div>
            <div className="sp-row">
              <select className="sp-inp" value={permissionUserId} onChange={(event) => setPermissionUserId(event.target.value)}>
                {users.map((user) => <option key={user.id} value={user.id}>{user.email}</option>)}
              </select>
              <select className="sp-inp" value={permissionCategory} onChange={(event) => setPermissionCategory(event.target.value)}>
                {settingsCategories.map((category) => (
                  <option key={category.category_key} value={category.category_key}>{category.label}</option>
                ))}
              </select>
              <select className="sp-inp" value={permissionForm} onChange={(event) => setPermissionForm(event.target.value as FormType)}>
                <option value="shorts">shorts</option>
              </select>
              <button className="btn-secondary btn-sm" onClick={addPermission} disabled={!permissionUserId}>권한 추가</button>
            </div>
            <div className="sp-key-status">
              {users.map((user) => (
                <div className="sp-user-row" key={user.id}>
                  <div className="sp-key-row">
                    <div className="sp-key-left">
                      <span className={`sp-kdot ${user.is_active ? 'on' : ''}`} />
                      <span className="sp-key-lbl">{user.email}</span>
                    </div>
                    <button className="mini-btn" onClick={() => toggleUser(user)}>
                      {user.is_active ? '활성' : '비활성'}
                    </button>
                  </div>
                  {user.role === 'editor' && (
                    <div className="sp-hint">
                      앱 {user.is_active ? '활성' : '비활성'} · Access 정책 별도 확인 · 활성 에셋 {user.active_asset_count || 0} · 오늘 추천 {user.today_recommendation_count || 0}
                      {Number(user.collection_error_count || 0) > 0 ? ` · 수집 오류 ${user.collection_error_count}` : ''}
                    </div>
                  )}
                  <div className="sp-permissions">
                    {(user.permissions || []).length ? (user.permissions || []).map((permission) => (
                      <button
                        className="mini-btn"
                        key={permission.id || `${permission.category_key}-${permission.form_type}`}
                        onClick={() => removePermission(user, permission.id)}
                        title="클릭하면 권한 해제"
                      >
                        {permission.category_key} · {permission.form_type} ✕
                      </button>
                    )) : <span className="sp-hint">권한 없음</span>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

function AuthPanel({
  error,
  devEmail,
  setDevEmail,
}: {
  error: string;
  devEmail: string;
  setDevEmail: (email: string) => void;
}) {
  const [draft, setDraft] = useState(devEmail);
  return (
    <section className="content auth-panel">
      <div className="empty-box">
        <div className="empty-title">접근 확인 필요</div>
        <div className="empty-meta">{error}</div>
        {import.meta.env.DEV && (
          <div className="auth-dev-row">
            <input className="sp-inp" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="local debug email" />
            <button
              className="btn-primary"
              onClick={() => {
                const next = draft.trim();
                if (next) localStorage.setItem('pick7.devEmail', next);
                else localStorage.removeItem('pick7.devEmail');
                setDevEmail(next);
              }}
            >
              적용
            </button>
          </div>
        )}
        {!import.meta.env.DEV && (
          <div className="auth-dev-row">
            <a className="btn-primary" href={`${API_BASE}/api/health`} target="_blank" rel="noreferrer">로그인 열기</a>
            <button className="btn-secondary" onClick={() => window.location.reload()}>다시 확인</button>
          </div>
        )}
      </div>
    </section>
  );
}

function SubTabs({
  tabs,
  active,
  onChange,
}: {
  tabs: [string, string][];
  active: string;
  onChange: (key: string) => void;
}) {
  return (
    <div className="sub-tabs">
      {tabs.map(([key, label]) => (
        <button key={key} className={`sub-tab ${active === key ? 'active' : ''}`} onClick={() => onChange(key)}>{label}</button>
      ))}
    </div>
  );
}

function ViewToggle({ value, onChange }: { value: 'card' | 'table'; onChange: (value: 'card' | 'table') => void }) {
  return (
    <div className="expl-view-btns" aria-label="보기 방식">
      <button className={`expl-view-btn ${value === 'card' ? 'active' : ''}`} onClick={() => onChange('card')}>▦</button>
      <button className={`expl-view-btn ${value === 'table' ? 'active' : ''}`} onClick={() => onChange('table')}>☰</button>
    </div>
  );
}

function Select({
  value,
  onChange,
  options,
  wide = false,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  options: string[][];
  wide?: boolean;
  disabled?: boolean;
}) {
  return (
    <select className={`rank-select ${wide ? 'wide' : 'slim'}`} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
      {options.map(([key, label]) => <option value={key} key={key}>{label}</option>)}
    </select>
  );
}

function NumberInput({ value, onChange, min, max }: { value: number; onChange: (value: number) => void; min: number; max: number }) {
  return (
    <label className="number-box">
      <span>결과</span>
      <input
        value={value}
        type="number"
        min={min}
        max={max}
        onChange={(event) => onChange(Math.max(min, Math.min(max, Number(event.target.value) || min)))}
      />
      <span>개</span>
    </label>
  );
}

function ConditionRow({
  condition,
  onPin,
  onDelete,
}: {
  condition: AssetCondition;
  onPin: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="condition-row">
      <div>
        <div className="condition-title">{assetTypeIcon(condition.condition_type)} {condition.label}</div>
        <div className="condition-meta">
          {condition.target === 'recommendation' ? '추천' : '예시'} · {condition.condition_type} · {condition.country || 'ALL'} · {condition.form_type} · {condition.limit_count}개
          {` · 순서 ${condition.sequence_order || condition.id}`}
          {condition.collection_status ? ` · ${assetCollectionStatusLabel(condition.collection_status)}` : ''}
          {condition.last_collected_at ? ` · ${formatUnixTime(condition.last_collected_at)}` : ''}
        </div>
        {condition.last_error ? <div className="condition-error">{condition.last_error}</div> : null}
      </div>
      <div className="row-actions">
        <button className={`mini-btn ${condition.is_pinned ? 'purple active' : ''}`} onClick={onPin}>{condition.is_pinned ? '고정됨' : '고정'}</button>
        <button className="mini-btn danger" onClick={onDelete}>삭제</button>
      </div>
    </div>
  );
}

function assetCollectionStatusLabel(status: string) {
  return ({ idle: '대기', pending: '수집 대기', running: '실행 중', done: '성공', failed: '실패' } as Record<string, string>)[status] || status;
}

function GuidelineStepAdmin({
  steps,
  onSaved,
  notify,
}: {
  steps: GuidelineStep[];
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  return (
    <div className="guide-editor">
      <div className="guide-editor-title">편집 순서 관리</div>
      {steps.map((step) => (
        <GuidelineStepEditRow key={step.id} step={step} onSaved={onSaved} notify={notify} />
      ))}
      <NewGuidelineStep nextStepNo={steps.length + 1} onSaved={onSaved} notify={notify} />
    </div>
  );
}

function GuidelineStepEditRow({
  step,
  onSaved,
  notify,
}: {
  step: GuidelineStep;
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState(step.title);
  const [description, setDescription] = useState(step.description);
  const [url, setUrl] = useState(step.url);
  const [buttonLabel, setButtonLabel] = useState(step.button_label);
  const [adminNote, setAdminNote] = useState(step.admin_note);
  const [enabled, setEnabled] = useState(Boolean(step.is_enabled));

  async function save() {
    try {
      await api.updateGuidelineStep(step.id, {
        title,
        description,
        url,
        buttonLabel,
        adminNote,
        isEnabled: enabled,
      });
      onSaved();
      notify({ tone: 'ok', text: '지침 스텝을 저장했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '지침 저장 실패' });
    }
  }

  return (
    <div className="guide-edit-row">
      <input className="rank-input tiny" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="제목" />
      <input className="rank-input grow" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="설명" />
      <input className="rank-input grow" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="링크" />
      <input className="rank-input tiny" value={buttonLabel} onChange={(event) => setButtonLabel(event.target.value)} placeholder="버튼" />
      <input className="rank-input grow" value={adminNote} onChange={(event) => setAdminNote(event.target.value)} placeholder="관리자 메모" />
      <label className="guide-check"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} /> 사용</label>
      <button className="mini-btn green" onClick={save}>저장</button>
    </div>
  );
}

function NewGuidelineStep({
  nextStepNo,
  onSaved,
  notify,
}: {
  nextStepNo: number;
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');

  async function create() {
    if (!title.trim()) return;
    try {
      await api.createGuidelineStep({
        stepNo: nextStepNo,
        title: title.trim(),
        description: '',
        url,
        buttonLabel: '열기',
        sortOrder: nextStepNo,
      });
      setTitle('');
      setUrl('');
      onSaved();
      notify({ tone: 'ok', text: '지침 스텝을 추가했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '스텝 추가 실패' });
    }
  }

  return (
    <div className="guide-edit-row">
      <input className="rank-input grow" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="새 스텝 제목" />
      <input className="rank-input grow" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="링크" />
      <button className="mini-btn green" onClick={create} disabled={!title.trim()}>스텝 추가</button>
    </div>
  );
}

function GuidelineToolAdmin({
  folders,
  onSaved,
  notify,
}: {
  folders: GuidelineFolder[];
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  return (
    <div className="guide-editor">
      <div className="guide-editor-title">도구 모음 관리</div>
      {folders.map((folder) => (
        <GuidelineFolderEditBlock key={folder.id} folder={folder} onSaved={onSaved} notify={notify} />
      ))}
      <NewGuidelineFolder onSaved={onSaved} notify={notify} />
    </div>
  );
}

function GuidelineFolderEditBlock({
  folder,
  onSaved,
  notify,
}: {
  folder: GuidelineFolder;
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState(folder.title);
  const [description, setDescription] = useState(folder.description);
  const [enabled, setEnabled] = useState(Boolean(folder.is_enabled));

  async function saveFolder() {
    try {
      await api.updateToolFolder(folder.id, { title, description, isEnabled: enabled });
      onSaved();
      notify({ tone: 'ok', text: '도구 폴더를 저장했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '폴더 저장 실패' });
    }
  }

  return (
    <div className="guide-folder-edit">
      <div className="guide-edit-row">
        <input className="rank-input tiny" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="폴더명" />
        <input className="rank-input grow" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="설명" />
        <label className="guide-check"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} /> 사용</label>
        <button className="mini-btn green" onClick={saveFolder}>폴더 저장</button>
      </div>
      {folder.tools.map((tool) => (
        <GuidelineToolEditRow key={tool.id} tool={tool} onSaved={onSaved} notify={notify} />
      ))}
      <NewGuidelineTool folderId={folder.id} onSaved={onSaved} notify={notify} />
    </div>
  );
}

function GuidelineToolEditRow({
  tool,
  onSaved,
  notify,
}: {
  tool: GuidelineTool;
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState(tool.title);
  const [url, setUrl] = useState(tool.url);
  const [buttonLabel, setButtonLabel] = useState(tool.button_label);
  const [adminNote, setAdminNote] = useState(tool.admin_note);
  const [enabled, setEnabled] = useState(Boolean(tool.is_enabled));

  async function save() {
    try {
      await api.updateGuidelineTool(tool.id, { title, url, buttonLabel, adminNote, isEnabled: enabled });
      onSaved();
      notify({ tone: 'ok', text: '도구를 저장했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '도구 저장 실패' });
    }
  }

  return (
    <div className="guide-edit-row tool">
      <input className="rank-input tiny" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="도구명" />
      <input className="rank-input grow" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="링크" />
      <input className="rank-input tiny" value={buttonLabel} onChange={(event) => setButtonLabel(event.target.value)} placeholder="버튼" />
      <input className="rank-input grow" value={adminNote} onChange={(event) => setAdminNote(event.target.value)} placeholder="관리자 메모" />
      <label className="guide-check"><input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} /> 사용</label>
      <button className="mini-btn green" onClick={save}>저장</button>
    </div>
  );
}

function NewGuidelineTool({
  folderId,
  onSaved,
  notify,
}: {
  folderId: number;
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');

  async function create() {
    if (!title.trim()) return;
    try {
      await api.createGuidelineTool({ folderId, title: title.trim(), url, buttonLabel: '열기' });
      setTitle('');
      setUrl('');
      onSaved();
      notify({ tone: 'ok', text: '도구를 추가했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '도구 추가 실패' });
    }
  }

  return (
    <div className="guide-edit-row tool">
      <input className="rank-input grow" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="새 도구명" />
      <input className="rank-input grow" value={url} onChange={(event) => setUrl(event.target.value)} placeholder="링크" />
      <button className="mini-btn green" onClick={create} disabled={!title.trim()}>도구 추가</button>
    </div>
  );
}

function NewGuidelineFolder({
  onSaved,
  notify,
}: {
  onSaved: () => void;
  notify: (notice: Notice) => void;
}) {
  const [title, setTitle] = useState('');

  async function create() {
    if (!title.trim()) return;
    try {
      await api.createToolFolder({ title: title.trim(), description: '' });
      setTitle('');
      onSaved();
      notify({ tone: 'ok', text: '도구 폴더를 추가했습니다' });
    } catch (error) {
      notify({ tone: 'error', text: error instanceof Error ? error.message : '폴더 추가 실패' });
    }
  }

  return (
    <div className="guide-edit-row">
      <input className="rank-input grow" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="새 폴더명" />
      <button className="mini-btn green" onClick={create} disabled={!title.trim()}>폴더 추가</button>
    </div>
  );
}

function StepNode({ step, index, onToggle }: { step: GuidelineStep; index: number; onToggle: () => void }) {
  const completed = Number(step.is_completed || 0) === 1;
  return (
    <div className={`step-node ${completed ? 'completed' : ''}`}>
      <div className="step-num">{step.step_no}</div>
      {index < 4 && <div className="step-line" />}
      <div className="step-card">
        <div className="step-title">{step.title}</div>
        <div className="step-desc">{step.description}</div>
        {step.admin_note && <div className="guide-note">{step.admin_note}</div>}
        {step.url && <a className="btn-secondary btn-sm" href={step.url} target="_blank" rel="noreferrer">{step.button_label}</a>}
        <button className={`mini-btn ${completed ? 'green' : ''}`} onClick={onToggle}>{completed ? '완료됨' : '완료 체크'}</button>
      </div>
    </div>
  );
}

function ToolFolder({ folder }: { folder: GuidelineFolder }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`tool-folder ${open ? 'open' : ''}`}>
      <button className="tool-folder-head" onClick={() => setOpen((value) => !value)}>
        <span>{folder.title}</span>
        <span>{open ? '접기' : '열기'}</span>
      </button>
      {open && (
        <div className="tool-list">
          {folder.tools.length ? folder.tools.map((tool) => (
            <div className="tool-item" key={tool.id}>
              <a className="tool-link" href={tool.url} target="_blank" rel="noreferrer">{tool.title}</a>
              {tool.admin_note && <div className="guide-note">{tool.admin_note}</div>}
            </div>
          )) : <div className="empty-meta">등록된 도구가 없습니다</div>}
        </div>
      )}
    </div>
  );
}

function RecentVideoStrip({ videos, compact = false }: { videos: { videoId: string; title: string; thumbnail: string; youtubeUrl?: string }[]; compact?: boolean }) {
  if (!videos.length) return <span className="empty-meta">최근 영상 없음</span>;
  return (
    <div className={`recent-strip ${compact ? 'compact' : ''}`}>
      {videos.slice(0, 3).map((video) => (
        <a key={video.videoId} className="recent-thumb" href={video.youtubeUrl || `https://www.youtube.com/watch?v=${video.videoId}`} target="_blank" rel="noreferrer" title={video.title}>
          {video.thumbnail ? <img src={video.thumbnail} alt="" /> : <span>🎞️</span>}
        </a>
      ))}
    </div>
  );
}

function MetricStrip({ item }: { item: VideoResult }) {
  return (
    <div className="metric-strip">
      <span>조회수 <b>{formatNumber(viewCount(item))}</b></span>
      <span>댓글 <b>{formatNumber(item.commentCount ?? item.comment_count)}</b></span>
      <span>좋아요 <b>{formatNumber(item.likeCount ?? item.like_count)}</b></span>
      <span>{formatDate(item.publishedAt || item.published_at)}</span>
    </div>
  );
}

function DataState<T>({ state, emptyTitle }: { state: LoadState<T>; emptyTitle: string }) {
  const count = useMemo(() => {
    const data = state.data as Record<string, unknown> | null;
    if (!data) return 0;
    for (const key of ['items', 'videos', 'runs', 'conditions', 'categories', 'users']) {
      const value = data[key];
      if (Array.isArray(value)) return value.length;
    }
    return 0;
  }, [state.data]);

  if (state.status === 'loading') return <EmptyPanel title="불러오는 중" />;
  if (state.status === 'error') return <ErrorBox text={state.error} />;
  if (!count) return <EmptyPanel title={emptyTitle} />;
  return <EmptyPanel title={`${count}개 결과`} />;
}

function EmptyPanel({ title }: { title: string }) {
  return (
    <div className="empty-box">
      <div className="empty-title">{title}</div>
    </div>
  );
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div className="empty-box danger">
      <div className="empty-title">불러오기 실패</div>
      <div className="empty-meta">{text}</div>
    </div>
  );
}

function useApiData<T>(key: string, loader: () => Promise<T>): LoadState<T> {
  const [state, setState] = useState<LoadState<T>>({ status: 'loading', data: null, error: '' });
  useEffect(() => {
    let active = true;
    setState({ status: 'loading', data: null, error: '' });
    loader()
      .then((data) => {
        if (active) setState({ status: 'ready', data, error: '' });
      })
      .catch((error) => {
        if (active) setState({ status: 'error', data: null, error: error instanceof Error ? error.message : `${key} failed` });
      });
    return () => { active = false; };
  }, [key]);
  return state;
}

function groupConditions(conditions: AssetCondition[]) {
  return conditions.reduce<Record<string, AssetCondition[]>>((acc, condition) => {
    const key = condition.condition_type;
    acc[key] ||= [];
    acc[key].push(condition);
    return acc;
  }, {});
}

function filterCategoriesForPermissions(categories: Category[], permissions: Permission[], formType: FormType) {
  const active = permissions.filter((permission) => (
    Number(permission.is_active ?? 1) === 1
    && (permission.form_type === formType || permission.form_type === ('all' as FormType))
  ));
  if (active.some((permission) => permission.category_key === 'all')) return categories;
  const allowed = new Set(active.map((permission) => permission.category_key));
  return categories.filter((category) => allowed.has(category.category_key));
}

function videosToKeywordChannels(items: VideoResult[]): ChannelResult[] {
  const byChannel = new Map<string, { channel: ChannelResult; videos: VideoResult[] }>();
  for (const item of items) {
    const channelId = resultChannelId(item);
    if (!channelId) continue;
    const current = byChannel.get(channelId);
    if (current) {
      current.videos.push(item);
      continue;
    }
    const title = resultChannelTitle(item) || channelId;
    byChannel.set(channelId, {
      channel: {
        rank: 0,
        source: 'youtube_keyword_channel',
        sourceName: 'YouTube Keyword Channel',
        itemType: 'channel',
        channelId,
        title,
        channelTitle: title,
        thumbnail: item.channelThumbnail || item.channel_thumbnail || '',
        subscriberCount: item.subscriberCount ?? item.subscriber_count ?? null,
        totalViewCount: 0,
        videoCount: 0,
        recentVideos: [],
        youtubeUrl: `https://www.youtube.com/channel/${channelId}`,
      },
      videos: [item],
    });
  }
  return [...byChannel.values()]
    .map(({ channel, videos }) => {
      const sortedVideos = [...videos].sort((a, b) => viewCount(b) - viewCount(a));
      const totalViews = sortedVideos.reduce((sum, video) => sum + viewCount(video), 0);
      return {
        ...channel,
        metricLabel: '키워드 조회수',
        metricValue: totalViews,
        totalViewCount: totalViews,
        videoCount: sortedVideos.length,
        recentVideos: sortedVideos.slice(0, 3).map((video) => ({
          videoId: videoId(video),
          title: video.title || '영상',
          thumbnail: thumb(video),
          publishedAt: video.publishedAt || video.published_at || '',
          youtubeUrl: videoUrl(video),
        })),
      };
    })
    .sort((a, b) => Number(b.metricValue || 0) - Number(a.metricValue || 0))
    .map((channel, index) => ({ ...channel, rank: index + 1 }));
}

function makeConditionLabel(input: {
  subtab: ExploreSubtab;
  query: string;
  country: string;
  categoryLabel: string;
  period: string;
  date?: string;
  sort: string;
  limit: number;
}) {
  const prefix = {
    channel: '채널',
    video: '영상 랭킹',
    keyword: input.query || '키워드',
    topic: '토픽',
    category: '카테고리',
  }[input.subtab];
  const parts = [prefix, input.country, input.categoryLabel, input.period];
  if (input.date) parts.push(input.date);
  parts.push(input.sort, `${input.limit}개`);
  return parts.join(' · ');
}

function assetTypeLabel(type: string) {
  if (type === 'channel') return '채널';
  if (type === 'keyword') return '키워드';
  if (type === 'ranking') return '랭킹';
  if (type === 'topic') return '토픽';
  if (type === 'category') return '카테고리';
  return type;
}

function assetTypeIcon(type: string) {
  if (type === 'channel') return '📡';
  if (type === 'keyword') return '#';
  if (type === 'ranking') return '🎞️';
  if (type === 'topic') return '📚';
  if (type === 'category') return '🗂️';
  return '•';
}

function videoId(item: VideoResult) {
  return item.videoId || item.video_id || '';
}

function resultChannelId(item: ChannelResult | VideoResult) {
  return 'channelId' in item && item.channelId
    ? item.channelId
    : 'channel_id' in item && item.channel_id
      ? item.channel_id
      : '';
}

function resultChannelTitle(item: ChannelResult | VideoResult) {
  if ('channelTitle' in item && item.channelTitle) return item.channelTitle;
  if ('title' in item && item.title && !('videoId' in item || 'video_id' in item)) return item.title;
  if ('channel_title' in item && item.channel_title) return item.channel_title;
  if ('channel_name' in item && item.channel_name) return item.channel_name;
  return '채널';
}

function videoUrl(item: VideoResult) {
  const id = videoId(item);
  return item.youtubeUrl || item.youtube_url || item.openUrl || (id ? `https://www.youtube.com/watch?v=${id}` : '#');
}

function channelUrl(item: VideoResult) {
  const id = item.channelId || item.channel_id || '';
  return id ? `https://www.youtube.com/channel/${id}` : '#';
}

function channelTitle(item: VideoResult) {
  return item.channelTitle || item.channel_title || item.channel_name || '채널';
}

function thumb(item: VideoResult) {
  return item.thumbnail || '';
}

function viewCount(item: VideoResult) {
  return item.viewCount ?? item.view_count ?? 0;
}

function formatScore(item: VideoResult) {
  return formatMetric(item.score ?? item.rps ?? 0);
}

function formatMetric(value: unknown) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '-';
  return Math.round(num * 10) / 10;
}

function formatNumber(value: unknown) {
  const num = Number(value || 0);
  if (!Number.isFinite(num) || num <= 0) return '-';
  if (num >= 100000000) return `${Math.round(num / 10000000) / 10}억`;
  if (num >= 10000) return `${Math.round(num / 1000) / 10}만`;
  return new Intl.NumberFormat('ko-KR').format(Math.round(num));
}

function formatDate(value: string | null | undefined) {
  if (!value) return '-';
  return String(value).slice(0, 10).replaceAll('-', '.');
}

function formatUnixTime(value: unknown) {
  const seconds = Number(value || 0);
  if (!Number.isFinite(seconds) || seconds <= 0) return '';
  const date = new Date(seconds * 1000 + 9 * 3600000);
  return `${date.toISOString().slice(0, 10).replaceAll('-', '.')} ${date.toISOString().slice(11, 16)}`;
}

function archiveStatusLabel(value: string | null | undefined) {
  if (value === 'editing') return '편집 중';
  if (value === 'completed') return '편집 완료';
  if (value === 'revision_requested') return '수정 요청';
  if (value === 'revising') return '수정 중';
  return value || '-';
}

function archiveEventLabel(event: ArchiveEvent) {
  if (event.event_type === 'archived') return '확정됨';
  if (event.event_type === 'note_updated') return '메모 수정';
  if (event.event_type === 'status_changed') {
    return `${archiveStatusLabel(event.from_value)} → ${archiveStatusLabel(event.to_value)}`;
  }
  return event.note || event.event_type;
}

function pathForTab(role: Role, tabKey: string) {
  const routes = role === 'admin' ? adminRoutes : editorRoutes;
  return routes[tabKey] || (role === 'admin' ? '/admin' : '/editor');
}

function resolveTabForPath(pathname: string, role: Role, tabs: NavigationTab[]) {
  const allowed = new Set(tabs.map((tab) => tab.key));
  const routes = role === 'admin' ? adminRoutes : editorRoutes;
  const matched = Object.entries(routes).find(([, path]) => pathname === path || pathname.startsWith(`${path}/`));
  if (matched && allowed.has(matched[0])) return matched[0];
  return tabs[0]?.key || 'recommendation';
}

function replaceAppPath(path: string) {
  if (window.location.pathname !== path) {
    window.history.replaceState(null, '', path);
  }
}

function channelRankingPinKey(userEmail: string, formType: FormType) {
  const owner = (userEmail || 'anonymous').trim().toLowerCase();
  return `ui.channelRankingPin.${owner}.${formType}`;
}

function parseChannelRankingPin(value: unknown, formType: FormType): ChannelRankingPin | null {
  if (!value || typeof value !== 'object') return null;
  const parsed = value as Partial<ChannelRankingPin>;
  if (!channelRankingSources.some(([source]) => source === parsed.source)) return null;
  return {
    source: String(parsed.source),
    country: String(parsed.country || 'US'),
    categoryKey: String(parsed.categoryKey || 'all'),
    period: String(parsed.period || 'daily'),
    date: String(parsed.date || ''),
    limit: Math.max(1, Math.min(20, Number(parsed.limit || 10))),
    formType,
    label: String(parsed.label || ''),
    savedAt: Number(parsed.savedAt || 0),
  };
}

function sameChannelRankingPin(a: ChannelRankingPin, b: ChannelRankingPin) {
  return a.source === b.source
    && a.country === b.country
    && a.categoryKey === b.categoryKey
    && a.period === b.period
    && (a.date || '') === (b.date || '')
    && a.limit === b.limit
    && a.formType === b.formType;
}

function videoArchivePayload(item: VideoResult) {
  return {
    video_id: videoId(item),
    title: item.title || '',
    channel_id: item.channelId || item.channel_id || '',
    channel_name: channelTitle(item),
    channel_avatar: item.channelThumbnail || item.channel_thumbnail || '',
    thumbnail: thumb(item),
    duration_sec: item.durationSeconds || item.duration_sec || 0,
    is_shorts: (item.durationSeconds || item.duration_sec || 0) < 240,
    view_count: viewCount(item),
    like_count: item.likeCount ?? item.like_count ?? 0,
    comment_count: item.commentCount ?? item.comment_count ?? 0,
    subscriber_count: item.subscriberCount ?? item.subscriber_count ?? 0,
    vph: item.vph || 0,
    outlier: item.outlierScore ?? item.outlier_score ?? item.outlier ?? 0,
    rps: item.score ?? item.rps ?? 0,
    published_at: item.publishedAt || item.published_at || null,
  };
}

function videoExamplePayload(item: VideoResult, editorEmail: string, formType: FormType, categoryKey: string) {
  return {
    video_id: videoId(item),
    title: item.title || '',
    channel_id: item.channelId || item.channel_id || '',
    channel_title: channelTitle(item),
    channel_thumbnail: item.channelThumbnail || item.channel_thumbnail || '',
    thumbnail: thumb(item),
    view_count: viewCount(item),
    like_count: item.likeCount ?? item.like_count ?? 0,
    comment_count: item.commentCount ?? item.comment_count ?? 0,
    published_at: item.publishedAt || item.published_at || null,
    added_for_email: editorEmail,
    form_type: formType,
    category_key: categoryKey || 'all',
  };
}

function todayKST() {
  return new Date(Date.now() + 9 * 3600000).toISOString().slice(0, 10);
}

export default App;
